/**
 * AdminRepActivity — Admin oversight of every rep's call/SMS activity, with
 * drill-down to individual call transcripts + AI-generated summaries.
 *
 * Routes:
 *   /admin/rep-activity  — main rollup
 *
 * Backend: GET /api/admin/rep-activity, GET /api/admin/compliance-queue,
 *          GET /api/admin/llm-usage, GET /api/dialer/call-transcript/{id}
 */
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { AgentMultiSelect } from "@/components/AgentMultiSelect";
import RcReconnectBanner from "@/components/rc/RcReconnectBanner";
import {
  Users, Phone, MessageSquare, Clock, TrendingUp, AlertTriangle,
  DollarSign, Search, ChevronRight, ChevronDown, X, Activity, Loader2,
  Sparkles, FileText, StickyNote, Mic, Square, Send, Trash2, Play, Pause, MessageCircle,
  ExternalLink, Mail, Voicemail, ArrowUpRight, ArrowDownLeft, ArrowLeft, CheckSquare, Check, BarChart3,
  RefreshCw,
} from "lucide-react";
import CallScorePanel from "@/components/CallScorePanel";
import PerformanceSnapshotPanel from "@/components/PerformanceSnapshotPanel";
import CoachingDrawer from "@/components/CoachingDrawer";
import HonorRateCard from "@/components/HonorRateCard";
// Iter 188.34 — LocksExpiringWidget removed from Rep Activity per Josh feedback.
// Iter 341 — shared date-filter → backend params translator so the
// row-expand drill-down always mirrors the summary count above it.
import { buildDateRangeParams } from "@/lib/dateRangeParams";
// Iter 343 — Products Discussed pills in the Review lightbox.
import ProductTagPills from "@/components/ProductTagPills";
import { CONTACT_ROLES, labelForRole, colorForRole } from "@/lib/contactRoles";
import { outcomeLabel as _canonicalOutcomeLabel } from "@/lib/outcomeLabels";
import { SUB_STATUSES } from "@/lib/pipelineStages";

// ─────────────────────────────────────────────────────────────────
// renderWhenTimes — Feb 2026 (Option A: enriched "When" cell)
// Given a row with `started_at`, `ended_at`, `duration_sec`, returns
// a two-line block showing local start→end times + wall-clock
// duration under the relative timestamp. Tooltip shows the absolute
// ISO+TZ for audit copy-paste. Used identically in both drill-downs.
// ─────────────────────────────────────────────────────────────────
function renderWhenTimes(row, testid) {
  const start = row.started_at || row.at;
  if (!start) return null;
  const s = new Date(start);
  if (isNaN(s.getTime())) return null;
  const endSrc = row.ended_at || (row.duration_sec > 0 ? new Date(s.getTime() + row.duration_sec * 1000).toISOString() : null);
  const e = endSrc ? new Date(endSrc) : null;
  const tfmt = { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true };
  const startTxt = s.toLocaleTimeString([], tfmt);
  const endTxt = e && !isNaN(e.getTime()) ? e.toLocaleTimeString([], tfmt) : null;
  // Wall-clock duration derived from timestamps (may differ from
  // stored duration_sec — that's actually the diagnostic value).
  let wallTxt = null;
  if (e && !isNaN(e.getTime())) {
    const wallSec = Math.max(0, Math.round((e.getTime() - s.getTime()) / 1000));
    if (wallSec > 0) {
      const mm = Math.floor(wallSec / 60);
      const ss = wallSec % 60;
      wallTxt = mm > 0 ? `${mm}m ${ss.toString().padStart(2, "0")}s wall` : `${ss}s wall`;
    }
  }
  const tzOffset = -s.getTimezoneOffset();
  const tzTxt = `${tzOffset >= 0 ? "+" : "-"}${String(Math.abs(Math.floor(tzOffset / 60))).padStart(2, "0")}:${String(Math.abs(tzOffset % 60)).padStart(2, "0")}`;
  const title = `Started (local ${tzTxt}): ${s.toLocaleString()}\nStarted (UTC): ${s.toISOString()}${e ? `\nEnded (local): ${e.toLocaleString()}` : ""}${e && !row.ended_at ? "\n\nEnded time derived from started_at + duration_sec (no transcript on file)." : ""}`;
  return (
    <div
      data-testid={testid}
      title={title}
      style={{ fontSize: 10, color: "#94a3b8", lineHeight: 1.35, marginTop: 2, cursor: "help" }}
    >
      <div style={{ fontVariantNumeric: "tabular-nums" }}>
        {startTxt}{endTxt ? ` → ${endTxt}` : ""}
      </div>
      {wallTxt && (
        <div style={{ color: "#64748b", fontSize: 9 }}>{wallTxt}</div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// RCRecordingPlayer — Feb 2026 (Rep Activity 0-second-duration audit)
// Streams an RC recording via the backend proxy (which attaches the
// rep's OAuth token) → blob URL → native <audio controls>. Lazy: no
// network happens until the manager clicks ▶, so 200-row tables don't
// blast RC on render. Mirrors the pattern used in LeadFocusTabs's
// call-log tab so the audio UX is identical everywhere.
// ─────────────────────────────────────────────────────────────────
function RCRecordingPlayer({ recordingId, agentId, token, testid }) {
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [audioUrl, setAudioUrl] = useState(null);
  // Iter 210.1 (Feb 2026) — track the last HTTP failure code so we
  // can render context-aware messaging: 401/403/404 → "Recording
  // expired — Refresh" (retry with force-refresh OAuth), everything
  // else → generic Retry.
  const [errorStatus, setErrorStatus] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const objectUrlRef = useRef(null);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) {
        try { URL.revokeObjectURL(objectUrlRef.current); } catch { /* ignore */ }
      }
    };
  }, []);

  const load = useCallback(async (forceRefresh = false) => {
    if (state === "loading" || state === "ready") return;
    setState("loading");
    setErrorStatus(null);
    try {
      const base = agentId
        ? `${API}/integrations/ringcentral/recording/${recordingId}?rep_id=${encodeURIComponent(agentId)}`
        : `${API}/integrations/ringcentral/recording/${recordingId}`;
      // On retry after an auth failure, force a fresh OAuth token
      // server-side. Prevents the "stale cached token → 401 → hidden
      // button" loop that April hit on Aug 18th.
      const endpoint = forceRefresh
        ? `${base}${base.includes("?") ? "&" : "?"}refresh=true`
        : base;
      const resp = await fetch(endpoint, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) {
        setErrorStatus(resp.status);
        const text = await resp.text();
        throw new Error(text || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      setAudioUrl(url);
      setState("ready");
    } catch (e) {
      setState("error");
      setAttempts((n) => n + 1);
      const msg = String(e?.message || e).slice(0, 120);
      // Only toast on non-auth errors — auth errors are common (short
      // RC session lifetime) and the "Refresh" button on the inline
      // control already communicates the problem without noise.
      const suppressToast = errorStatus === 401 || errorStatus === 403;
      if (!suppressToast) {
        toast.error("Could not load recording", { description: msg });
      }
    }
  }, [recordingId, agentId, token, state, errorStatus]);

  if (state === "ready" && audioUrl) {
    return (
      <audio
        data-testid={testid || `rc-audio-${recordingId}`}
        controls
        src={audioUrl}
        style={{ height: 26, maxWidth: 200 }}
      />
    );
  }

  // Iter 210.1 — context-aware error messaging.
  const isExpired = state === "error" && (errorStatus === 401 || errorStatus === 403);
  const isMissing = state === "error" && errorStatus === 404;
  const shouldForceRefresh = state === "error" && (isExpired || attempts >= 1);
  const errorTitle = isExpired
    ? "RC OAuth token expired for this rep. Click to force-refresh and retry."
    : isMissing
      ? "RC no longer has this recording (may have been purged). Retry once in case it's a transient 404."
      : state === "error"
        ? `Recording fetch failed (HTTP ${errorStatus || "?"}) — click to retry.`
        : `Play RC recording ${recordingId}`;

  return (
    <button
      data-testid={testid || `rc-play-${recordingId}`}
      onClick={(e) => { e.stopPropagation(); load(shouldForceRefresh); }}
      disabled={state === "loading"}
      title={errorTitle}
      style={{
        display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 600,
        color: state === "error"
          ? (isExpired ? "#b45309" : "#c9302c")
          : "#0071E3",
        background: state === "error" && isExpired ? "#fef3c7" : "#fff",
        border: `0.5px solid ${state === "error" && isExpired ? "#fcd34d" : "#c7d2fe"}`,
        borderRadius: 6,
        padding: "3px 8px", cursor: state === "loading" ? "wait" : "pointer",
        fontFamily: "inherit",
      }}
    >
      {state === "loading"
        ? <><Loader2 style={{ width: 10, height: 10, animation: "spin 1s linear infinite" }} /> Loading…</>
        : isExpired
          ? <>↻ Refresh</>
          : isMissing
            ? <>Retry (404)</>
            : state === "error"
              ? <>Retry ▶</>
              : <><Play style={{ width: 10, height: 10 }} /> Play</>}
    </button>
  );
}

// Copy-to-clipboard chip — used for RC call-log id / recording id.
function CopyableId({ value, label, testid }) {
  const [copied, setCopied] = useState(false);
  if (!value) return <span style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>;
  const short = String(value).length > 10 ? `…${String(value).slice(-8)}` : String(value);
  return (
    <button
      data-testid={testid}
      onClick={(e) => {
        e.stopPropagation();
        try {
          navigator.clipboard.writeText(String(value));
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        } catch { /* ignore */ }
      }}
      title={`${label || "ID"}: ${value}  (click to copy)`}
      style={{
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 10,
        color: copied ? "#166534" : "#475569",
        background: copied ? "#dcfce7" : "#f1f5f9",
        border: "0.5px solid #e2e8f0", borderRadius: 4,
        padding: "1px 6px", cursor: "pointer",
      }}
    >
      {copied ? "✓ copied" : short}
    </button>
  );
}


// Iter 174.2 (Danny — granular Outcome column). Map every "Contacted —"
// sub-status to its tier so we can color-code the chip exactly like the
// Lead Focus sub-status picker (gatekeeper grey, admin blue, clinical
// green, executive purple, no-live-contact muted).
// Iter 175.6 + 179.13 (Ioannis #c9af6f1f / #bbc12e95 / #5cc7fb29 /
// #edf0e524 Feb 2026) — Build a lookup of EVERY known sub-status (not
// just Contacted — gatekeeper/admin/clinical/executive) so the Rep
// Activity row can render the tiered chip for "Left Voicemail",
// "Needs Follow-Up" and any other no_contact-tier value. Previously
// only `/^Contacted —/i.test(sub)` was checked, so a lead marked
// "Left Voicemail" with a connected-outcome activity row fell to
// the amber "Tag pending" placeholder — which is what Ioannis kept
// flagging ("substatus says left vm, report says tag pending").
const SUB_STATUS_TIER_BY_VALUE = (() => {
  const out = {};
  for (const s of (SUB_STATUSES?.contacted || [])) {
    out[s.value] = s.tier;
  }
  // Fan in non-`contacted`-stage sub-statuses too (demo, post_demo, etc.)
  // tagged at a default tier so they render with a sensible color even
  // when the call's stage advanced.
  for (const stageKey of Object.keys(SUB_STATUSES || {})) {
    if (stageKey === "contacted") continue;
    for (const s of (SUB_STATUSES[stageKey] || [])) {
      if (!out[s.value]) out[s.value] = s.tier || "no_contact";
    }
  }
  return out;
})();
const TIER_STYLE = {
  gatekeeper: { bg: "#f1f5f9", color: "#475569", border: "#cbd5e1" },
  admin:      { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
  clinical:   { bg: "#dcfce7", color: "#166534", border: "#86efac" },
  executive:  { bg: "#ede9fe", color: "#6d28d9", border: "#c4b5fd" },
  no_contact: { bg: "#f1f5f9", color: "#64748b", border: "#cbd5e1" },
};
// Compact display label for the chip — strip the "Contacted — " prefix
// so the pill fits in the column without truncation. The full label
// stays in the tooltip.
// Iter 179.13 — abbreviate no_contact tier labels too ("Left Voicemail"
// → "Left VM", "Needs Follow-Up" → "Follow-up") so they render cleanly
// in the Outcome column at the same width as the Contacted chips.
const subStatusShortLabel = (sub) => {
  if (!sub) return null;
  const SHORT = {
    "Left Voicemail": "Left VM",
    "Needs Follow-Up": "Follow-up",
  };
  if (SHORT[sub]) return SHORT[sub];
  return sub.replace(/^Contacted —\s*/i, "Contacted: ");
};
// Iter 179.13 — tighter color set for the no_contact tier so "Left
// Voicemail" / "Needs Follow-Up" don't share the grey "gatekeeper"
// styling (which Ioannis confused with "untagged"). Amber-tinted to
// signal "action still needed" without the alarming red of a fail.
const NO_CONTACT_STYLE = { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => {
  const t = localStorage.getItem("access_token") || localStorage.getItem("token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtSecs = (s) => {
  if (!s) return "—";
  const m = Math.floor(s / 60), sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};
const fmtUSD = (n) => `$${(n || 0).toFixed(2)}`;

// Iter 188.34 — CSV export helpers for the Rep Activity Report.
// `csvEscape` wraps values that contain quotes/commas/newlines in
// double quotes and escapes embedded quotes per RFC 4180. `downloadCSV`
// serialises an array of header labels + row arrays into a Blob that
// the browser saves to disk with the given filename.
const csvEscape = (v) => {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};
const downloadCSV = (filename, headers, rows) => {
  const lines = [headers.map(csvEscape).join(",")];
  for (const r of rows) lines.push(r.map(csvEscape).join(","));
  const blob = new Blob(["\ufeff" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 400);
};
const slugifyForFilename = (s) => String(s || "rep").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 40) || "rep";
const todayStamp = () => new Date().toISOString().slice(0, 10);

// EHI tab CSVs: drop lead-only columns and rename lead wording so files match the screen.
function _ehiCsv(headers, rows, dropCols, rename) {
  const keep = headers.map((h, i) => (dropCols.includes(h) ? -1 : i)).filter((i) => i >= 0);
  return [keep.map((i) => rename[headers[i]] || headers[i]), rows.map((r) => keep.map((i) => r[i]))];
}

export default function AdminRepActivity({ embedded = false, scope = "crm" }) {
  // scope="ehi" → EHI Clients → Rep Activity tab (EHI client calls only, admin-only).
  const isEhi = scope === "ehi";
  const scopeParams = isEhi ? { scope: "ehi" } : {};
  // Iter 179.12 (Lisa #c4ec2214 Feb 2026) — non-admin reps now see this
  // page too (filtered to their own data server-side). We branch UI on
  // `isManager` to:
  //   • skip the admin-only Compliance + LLM Usage endpoints (would 403)
  //   • hide the rollup search bar + multi-row table (only one row)
  //   • auto-expand the rep's own row so calls are visible immediately
  //   • hide manager-only nav (Coaching, RC connections, AI tuning)
  const { user: _authUser, token } = useAuth() || { user: null, token: null };
  // Tolerant role canonicalization (Jun 2026 — Hamza): strip ALL
  // non-alphanumeric chars so "Super Admin", "super-admin", "SUPER_ADMIN"
  // and "superadmin" all resolve to the same canonical key. Previously we
  // only .toLowerCase()'d, so a stored role of "Super Admin" became
  // "super admin" (space, not underscore) and failed the includes() check
  // — hiding the multi-select agent filter (and the all-reps table) from a
  // legitimate Super Admin. The filter's visibility is bound to `isManager`
  // (the same flag that grants the all-reps view), so they can never drift.
  const _normRole = (r) => String(r || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const _role = _normRole(_authUser?.role);
  const isManager = ["superadmin", "crmadmin", "salesmanager", "marketingmanager"].includes(_role);
  // Iter 221 (Jun 2026 · Rahul) — the Compliance-queue + LLM-usage endpoints
  // are ADMIN-only on the backend (super_admin / crm_admin). Sales/Marketing
  // Managers are `isManager` but 403 on them, so gate those calls on `isAdmin`
  // (not `isManager`) to avoid pointless 403s that used to blank the whole
  // Rep Activity rollup.
  const isAdmin = ["superadmin", "crmadmin"].includes(_role);
  const selfAgentId = _authUser?.id || null;
  // Iter 167 (Bug #6 — Danny report): added Yesterday preset and custom
  // date picker. `range` is the preset id; when range="custom", we send
  // `date_from` / `date_to` to the backend instead of `days`.
  //
  // Iter 170 (Danny rep_activity_drill_down_spec.md, Josh approved
  // 2026-06-05). The Dashboard Team Performance card now passes
  // `?rep=<agent_id>&range=<preset>&snapshot=1` so the page lands
  // pre-filtered to one rep with the Performance Snapshot panel auto-open.
  // URL is the source of truth — refresh keeps the state.
  const navigate = useNavigate();
  const location = useLocation();
  const initialParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialRep = initialParams.get("rep");
  const initialRangeRaw = initialParams.get("range");
  const initialSnapshot = initialParams.get("snapshot") === "1";
  const initialFrom = initialParams.get("from") || "";
  const initialTo = initialParams.get("to") || "";

  // Normalize the `range` query param into our internal range vocabulary.
  const normRange = (() => {
    if (!initialRangeRaw) return "7";
    const r = initialRangeRaw.toLowerCase();
    if (r === "today" || r === "0") return "today";
    if (r === "yesterday") return "yesterday";
    if (r === "last_7_days" || r === "7") return "7";
    if (r === "last_30_days" || r === "30") return "30";
    if (r === "last_90_days" || r === "90") return "90";
    if (initialFrom && initialTo) return "custom";
    return "7";
  })();
  const initialDays = normRange === "today" ? 0
                    : normRange === "7" ? 7
                    : normRange === "30" ? 30
                    : normRange === "90" ? 90
                    : 7;

  const [days, setDays] = useState(initialDays);
  const [range, setRange] = useState(normRange);
  const [customFrom, setCustomFrom] = useState(initialFrom);
  const [customTo, setCustomTo] = useState(initialTo);
  const [tab, setTab] = useState("rollup"); // rollup | compliance | usage
  const [rollup, setRollup] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [compliance, setCompliance] = useState([]);
  const [usage, setUsage] = useState({ rows: [], total_cost_usd: 0 });
  const [loading, setLoading] = useState(false);
  const [openTranscriptId, setOpenTranscriptId] = useState(null);
  const [openTranscript, setOpenTranscript] = useState(null);
  // Iter 170 — when arriving from the Dashboard Team Performance card with
  // `?rep=...`, pin the rollup filter to that one rep so the page opens
  // scoped (not the whole team).
  const [search, setSearch] = useState(initialRep ? "" : "");
  const [pinnedRepId, setPinnedRepId] = useState(initialRep || null);
  // Multi-select agent filter (Jun 2026). Holds agent_ids; empty => all.
  // Narrows the rollup client-side (backend already returns all reps).
  // Coexists with the single-agent `search` box (both apply together).
  const [selectedAgentIds, setSelectedAgentIds] = useState([]);
  // Expandable row drill-down state
  const [expandedAgentId, setExpandedAgentId] = useState(null);
  const [agentDetail, setAgentDetail] = useState(null); // { agent_id, rows: [...], summary, loading }
  // Per-call disposition lightbox (Iter 141 — super admin call review)
  const [callDetail, setCallDetail] = useState(null); // { open, loading, data, agent_id, lead_id, external_id, activity_id }
  const [callAISummary, setCallAISummary] = useState(null); // { loading, data, error }
  // Iter 170 — Performance Snapshot slide-out panel state.
  const [snapshotOpen, setSnapshotOpen] = useState(initialSnapshot);
  const [snapshotRep, setSnapshotRep] = useState(null);
  const [snapshotCalls, setSnapshotCalls] = useState([]);
  const [snapshotCallsLoading, setSnapshotCallsLoading] = useState(false);
  // Iter 342 — accurate full-window call count for the Snapshot panel's
  // "Showing first 50 of N" copy. Backend returns this on every response.
  const [snapshotCallsTotal, setSnapshotCallsTotal] = useState(0);
  // Iter 193 (Vipal — CCM Coaching Agent) — Lumina coaching drawer.
  const [coachingOpen, setCoachingOpen] = useState(false);
  const [coachingRep, setCoachingRep] = useState(null);
  const cameFromDashboard = initialSnapshot && initialRep;

  const load = async () => {
    setLoading(true);
    try {
      // Iter 167 (Bug #6): build params from the selected range. Custom
      // ranges and "yesterday" go through date_from/date_to; the canned
      // presets keep using the `days` param for backwards compat.
      //
      // Iter 183 (Lisa #ecfcf611 Feb 2026) — "today" was missing from
      // this branch, so picking Today returned a 7-day window. We now
      // pin both today and yesterday explicitly.
      //
      // Iter 219.1 (Aug 2026 · Bug — same UTC-drift class as the
      // shared `buildDateRangeParams` helper, but this inline copy
      // was missed in the first pass). `d.toISOString().slice(0,10)`
      // returns the UTC calendar day; for any US-timezone rep after
      // ~4-8 PM local, that returned TOMORROW's UTC date and the
      // "Today" filter returned 0 rows even though 500+ calls had
      // been made that day. Now uses the rep's LOCAL calendar day so
      // the backend UTC window aligns with what the rep sees on
      // their wall clock.
      const params = {};
      const iso = (d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
      if (range === "custom") {
        if (!customFrom || !customTo) {
          setLoading(false);
          toast.error("Pick a start and end date for the custom range");
          return;
        }
        params.date_from = customFrom;
        params.date_to = customTo;
      } else if (range === "today") {
        const t = new Date();
        params.date_from = iso(t);
        params.date_to = iso(t);
      } else if (range === "yesterday") {
        const y = new Date();
        y.setDate(y.getDate() - 1);
        params.date_from = iso(y);
        params.date_to = iso(y);
      } else {
        params.days = days;
      }
      const promises = [
        axios.get(`${API}/admin/rep-activity`, { params: { ...params, ...scopeParams }, headers: authHeaders() }),
      ];
      if (isAdmin && !isEhi) {
        promises.push(axios.get(`${API}/admin/compliance-queue`, { params: { days }, headers: authHeaders() }));
        promises.push(axios.get(`${API}/admin/llm-usage`, { params: { days }, headers: authHeaders() }));
      }
      // Iter 221 (Jun 2026 · Rahul) — allSettled, not all. Even gated on
      // isAdmin, a transient 403/timeout on the admin-only extras must never
      // reject the batch and blank the rep-activity rollup (the primary data
      // a Sales/Marketing Manager comes here for).
      const results = await Promise.allSettled(promises);
      const raRes = results[0];
      if (raRes.status !== "fulfilled") {
        throw raRes.reason;
      }
      const ra = raRes.value;
      // Iter 221 (Jun 2026 · Rahul) — non-admins see ONLY their own Rep
      // Performance row. The backend already force-scopes the rollup, but we
      // filter here too as defense-in-depth so no other rep's row can appear
      // on this table by any means. Managers/admins see every rep (unchanged).
      const _rawRollup = ra.data.rollup || [];
      setRollup(isManager ? _rawRollup : _rawRollup.filter(r => r.agent_id === selfAgentId));
      setTranscripts(ra.data.recent_transcripts || []);
      if (isAdmin && !isEhi) {
        const compRes = results[1];
        const usageRes = results[2];
        setCompliance(compRes && compRes.status === "fulfilled" ? (compRes.value.data.flagged || []) : []);
        setUsage(usageRes && usageRes.status === "fulfilled" ? (usageRes.value.data || { rows: [], total_cost_usd: 0 }) : { rows: [], total_cost_usd: 0 });
      } else {
        setCompliance([]);
        setUsage({ rows: [], total_cost_usd: 0 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load reports");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); setExpandedAgentId(null); setAgentDetail(null); /* eslint-disable-next-line */ }, [days, range, customFrom, customTo]);

  // Iter 179.12 (Lisa #c4ec2214) — auto-expand the rep's own row after
  // load so they don't have to click the row to see their calls. The
  // server-side scope guarantees `rollup` contains at most one row
  // (theirs) for non-managers; this just opens it.
  useEffect(() => {
    if (!isManager && selfAgentId && rollup.length > 0 && !expandedAgentId) {
      const myRow = rollup.find(r => r.agent_id === selfAgentId) || rollup[0];
      if (myRow?.agent_id) {
        toggleAgentRow(myRow.agent_id);
      }
    }
    // eslint-disable-next-line
  }, [rollup, isManager, selfAgentId]);

  const openTranscript_ = async (extId) => {
    setOpenTranscriptId(extId);
    setOpenTranscript(null);
    try {
      const { data } = await axios.get(`${API}/dialer/call-transcript/${extId}`, { headers: authHeaders() });
      setOpenTranscript(data);
    } catch (e) {
      // Iter 159 (Josh): surface the FastAPI `detail` (WebPhone vs RC guidance)
      // instead of a generic "Transcript not available" toast.
      // Iter 177.5 (Danny #ffa8855a Feb 2026) — the backend now returns
      // a structured object `{code, message, attempts, last_error}` for
      // 202 (in-progress) and 503 (transcription_abandoned) so we can
      // tell the rep whether to wait or to ask an admin to retry.
      const detail = e?.response?.data?.detail;
      let msg;
      if (detail && typeof detail === "object" && detail.message) {
        msg = detail.message;
      } else {
        msg = detail || e?.response?.data?.message || e?.message || "Transcript not available";
      }
      toast.error(msg);
    }
  };

  // Iter 342 — pagination state for the row-expand drill-down. Defaults
  // to page 1 with 50 rows/page. Reset to page 1 whenever the date
  // filter or expanded rep changes (see the useEffect below).
  const [agentDetailPage, setAgentDetailPage] = useState(1);
  const AGENT_DETAIL_PAGE_SIZE = 50;

  const fetchAgentDetailPage = useCallback(async (agentId, pageNum) => {
    setAgentDetail((prev) => ({ ...(prev || { agent_id: agentId, rows: [], summary: {} }), loading: true }));
    try {
      const dateParams = buildDateRangeParams({ range, customFrom, customTo, days });
      const { data } = await axios.get(`${API}/admin/rep-activity/${agentId}/calls`, {
        params: { ...dateParams, ...scopeParams, page: pageNum, page_size: AGENT_DETAIL_PAGE_SIZE },
        headers: authHeaders(),
      });
      setAgentDetail({ ...data, loading: false });
    } catch (e) {
      toast.error("Couldn't load call detail for this rep");
      setAgentDetail({ agent_id: agentId, rows: [], summary: {}, loading: false, error: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, customFrom, customTo, days, scope]);

  const toggleAgentRow = async (agentId) => {
    if (!agentId) return;
    if (expandedAgentId === agentId) {
      setExpandedAgentId(null);
      return;
    }
    setExpandedAgentId(agentId);
    setAgentDetail({ agent_id: agentId, rows: [], summary: {}, loading: true });
    setAgentDetailPage(1);
    await fetchAgentDetailPage(agentId, 1);
  };

  // Iter 342 — when the user changes the date filter while a row is
  // expanded, refetch page 1 for the visible rep so stale rows don't
  // leak across filter changes.
  useEffect(() => {
    if (expandedAgentId) {
      setAgentDetailPage(1);
      fetchAgentDetailPage(expandedAgentId, 1);
    }
  }, [range, customFrom, customTo, days, expandedAgentId, fetchAgentDetailPage]);

  const goToAgentDetailPage = useCallback(async (pageNum) => {
    if (!expandedAgentId || !agentDetail) return;
    const totalPages = agentDetail.total_pages || 1;
    const clamped = Math.max(1, Math.min(pageNum, totalPages));
    if (clamped === agentDetailPage) return;
    setAgentDetailPage(clamped);
    await fetchAgentDetailPage(expandedAgentId, clamped);
  }, [expandedAgentId, agentDetail, agentDetailPage, fetchAgentDetailPage]);

  // Export the FULL call-log detail for one rep across every page of the
  // date range. The server caps pages at 500 raw calls (then merges rapid
  // retries), so loop on total_pages; any failed page aborts the export.
  const exportRepDetailCSV = useCallback(async (agentId, agentName) => {
    if (!agentId) return;
    const toastId = toast.loading("Preparing CSV…");
    try {
      const dateParams = buildDateRangeParams({ range, customFrom, customTo, days });
      const PAGE_SIZE = 500;
      const MAX_CALLS = 50000;
      const fetchPage = (page) => axios.get(`${API}/admin/rep-activity/${agentId}/calls`, {
        params: { ...dateParams, ...scopeParams, page, page_size: PAGE_SIZE },
        headers: authHeaders(),
      }).then((res) => res.data || {});
      const first = await fetchPage(1);
      if ((first.total || 0) > MAX_CALLS) {
        toast.dismiss(toastId);
        toast.error(`This range has ${Number(first.total).toLocaleString()} calls — please narrow the date range to 50,000 or fewer`);
        return;
      }
      const totalPages = Math.max(1, Number(first.total_pages) || 1);
      const pages = [first.rows || []];
      for (let p = 2; p <= totalPages; p += 4) {
        const batch = [];
        for (let q = p; q < p + 4 && q <= totalPages; q += 1) batch.push(fetchPage(q));
        const results = await Promise.all(batch);
        results.forEach((d) => pages.push(d.rows || []));
      }
      const seen = new Set();
      const rows = [];
      pages.flat().forEach((r) => {
        const key = r.id || r.external_id || `${r.at}|${r.lead_id}|${r.kind}`;
        if (seen.has(key)) return;
        seen.add(key);
        rows.push(r);
      });
      // Export the rep's call log exactly as displayed. CSV columns MIRROR
      // the on-screen 12 data columns 1:1: When · Lead · Phone · Call outcome ·
      // True Outcome · Human Connect · Sub-status · Contact · Duration ·
      // Recording · Sentiment · Notes / summary. Each column formats the same
      // way it appears on screen (fmtSecs for Duration, outcomeLabel for
      // Outcome, True/False chip for Human Connect, labelForRole for Contact,
      // subStatusShortLabel for Sub-status).
      const _fmtWhen = (v) => (v ? new Date(v).toLocaleString() : "");
      const _fmtLead = (r) => {
        const name = (r.lead_name || "").trim();
        const practice = (r.practice || "").trim();
        return practice && practice !== name ? `${name} · ${practice}` : name;
      };
      const _fmtCallOutcome = (r) => {
        // Iter 221 (Jun 2026 · Rahul) — "Call outcome" = the phone-system
        // (RingCentral) result, from the raw `rc_outcome` the backend
        // preserves before the disposition override. Label only, mirroring
        // the on-screen chip. Falls back to `outcome` for older payloads.
        if (r.kind !== "call") return (r.kind || "").toUpperCase();
        const raw = String(r.rc_outcome || r.outcome || "").trim();
        return raw ? _canonicalOutcomeLabel(raw) : "";
      };
      const _fmtTrueOutcome = (r) => {
        // Iter 221 — "True Outcome" = the outcome the rep logged on this
        // call's after-call-work (disposition) entry. Empty when the call
        // was never dispositioned (mirrors the "—" on screen).
        if (r.kind !== "call" || r.outcome_source !== "disposition") return "";
        const raw = String(r.outcome || "").trim();
        if (!raw) return "";
        const label = _canonicalOutcomeLabel(raw, r.outcome_detail || undefined);
        return r.outcome_detail
          ? `${label} — ${String(r.outcome_detail).replace(/_/g, " ")}`
          : label;
      };
      const _fmtSubStatus = (r) => {
        // Iter 221 — per-call disposition sub-status (`substatus`), not the
        // lead's global `sub_status` (which showed a stale "Quote Sent" on
        // every row). Empty when this call has no logged sub-status.
        if (r.kind !== "call") return "";
        const raw = (r.substatus || "").trim();
        return raw ? subStatusShortLabel(raw) : "";
      };
      const _fmtContact = (r) => {
        if (r.kind !== "call") return "";
        // Mirror the on-screen "reached a human?" gate — Contact chip
        // only renders for outcomes that actually reached a human.
        const HUMAN = new Set(["connected", "demo-booked", "demo_booked", "callback", "left_on_hold"]);
        const reachedHuman = r.is_live_human === true || HUMAN.has(r.outcome);
        if (!reachedHuman || !r.contact_role) return "";
        const label = labelForRole(r.contact_role) || r.contact_role;
        return r.is_decision_maker ? `${label} (DM)` : label;
      };
      const _fmtDuration = (r) => (r.kind === "call" ? fmtSecs(r.duration_sec) : "");
      const _fmtRecording = (r) => {
        // On-screen shows a Play button when a recording exists,
        // otherwise "—". In the CSV, surface the actual URL so
        // auditors can jump straight to the media in RC. Empty
        // string when no recording.
        if (r.kind !== "call") return "";
        const url = r.recording_url || "";
        if (url) return url;
        const id = r.recording_id;
        return id ? String(id) : "";
      };
      const _fmtNotes = (r) => (
        r.notes_preview
        || (Array.isArray(r.agent_notes) && r.agent_notes[0]?.text)
        || ""
      );

      const _fmtHumanConnect = (r) => {
        // Mirrors the on-screen "Human Connect" chip: True / False for a
        // classified call, blank (screen shows "—") for non-calls or
        // not-yet-classified calls.
        if (r.kind !== "call") return "";
        if (r.human_connect_verdict === true) return "True";
        if (r.human_connect_verdict === false) return "False";
        return "";
      };

      const headers = [
        "When", "Lead", "Phone", "Call outcome", "True Outcome", "Human Connect", "Sub-status", "Contact",
        "Duration", "Recording", "Sentiment", "Notes / summary",
      ];
      const csvRows = rows.map((r) => [
        _fmtWhen(r.at),
        _fmtLead(r),
        r.to_number || r.from_number || "",
        _fmtCallOutcome(r),
        _fmtTrueOutcome(r),
        _fmtHumanConnect(r),
        _fmtSubStatus(r),
        _fmtContact(r),
        _fmtDuration(r),
        _fmtRecording(r),
        r.sentiment || "",
        _fmtNotes(r),
      ]);
      const slug = slugifyForFilename(agentName || agentId);
      const [outHeaders, outRows] = isEhi
        ? _ehiCsv(headers, csvRows, ["Sub-status"], { Lead: "Client" })
        : [headers, csvRows];
      downloadCSV(`${isEhi ? "ehi-" : ""}rep-activity_${slug}_${todayStamp()}.csv`, outHeaders, outRows);
      toast.dismiss(toastId);
      toast.success(`Exported ${csvRows.length.toLocaleString()} row${csvRows.length === 1 ? "" : "s"} for ${agentName || "rep"}`);
    } catch (e) {
      toast.dismiss(toastId);
      toast.error("Couldn't export CSV — please try again");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, customFrom, customTo, days, isEhi]);

  // Iter 141 — open the per-call disposition lightbox
  const openCallDetail = async (row, agentId) => {
    if (!row) return;
    const ctx = {
      open: true, loading: true, data: null,
      agent_id: agentId, lead_id: row.lead_id,
      external_id: row.external_id, activity_id: row.id,
      human_connect_verdict: row.human_connect_verdict,
    };
    setCallDetail(ctx);
    setCallAISummary({ loading: true, data: null });
    try {
      const { data } = await axios.get(`${API}/admin/rep-activity/call-detail`, {
        params: {
          agent_id: agentId,
          external_id: row.external_id || undefined,
          activity_id: row.id || undefined,
          lead_id: row.lead_id || undefined,
        },
        headers: authHeaders(),
      });
      setCallDetail((prev) => prev ? { ...prev, loading: false, data } : null);
    } catch (e) {
      toast.error("Couldn't load call detail");
      setCallDetail((prev) => prev ? { ...prev, loading: false, data: null, error: true } : null);
      setCallAISummary(null);
      return;
    }
    // Auto-load the AI summary in parallel (Iter 141 follow-up — user request).
    try {
      const { data } = await axios.post(`${API}/admin/rep-activity/per-call-ai-summary`, {
        agent_id: ctx.agent_id,
        external_id: ctx.external_id || undefined,
        activity_id: ctx.activity_id || undefined,
        lead_id: ctx.lead_id || undefined,
      }, { headers: authHeaders() });
      setCallAISummary({ loading: false, data });
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI summary failed";
      setCallAISummary({ loading: false, error: msg });
    }
  };

  const runCallAISummary = async () => {
    if (!callDetail || !callDetail.agent_id) return;
    setCallAISummary({ loading: true, data: null });
    try {
      // Iter 147 — when the manager explicitly clicks "Regenerate" we
      // pass force:true so the backend skips its cache and re-runs the
      // LLM. The first auto-load (above) does NOT pass force, so it
      // serves from cache when nothing has changed → saves Azure spend.
      const { data } = await axios.post(`${API}/admin/rep-activity/per-call-ai-summary`, {
        agent_id: callDetail.agent_id,
        external_id: callDetail.external_id || undefined,
        activity_id: callDetail.activity_id || undefined,
        lead_id: callDetail.lead_id || undefined,
        force: true,
      }, { headers: authHeaders() });
      setCallAISummary({ loading: false, data });
    } catch (e) {
      const msg = e?.response?.data?.detail || "AI summary failed";
      toast.error(msg);
      setCallAISummary({ loading: false, error: msg });
    }
  };

  const filteredRollup = useMemo(() => {
    const sel = new Set(selectedAgentIds);
    return rollup.filter((r) => {
      if (pinnedRepId && r.agent_id !== pinnedRepId) return false;
      if (sel.size && !sel.has(r.agent_id)) return false;
      if (!search) return true;
      return (r.agent_name || "").toLowerCase().includes(search.toLowerCase()) ||
             (r.agent_email || "").toLowerCase().includes(search.toLowerCase());
    }).sort((a, b) => (b.merged_calls ?? b.calls ?? 0) - (a.merged_calls ?? a.calls ?? 0));
  }, [rollup, search, pinnedRepId, selectedAgentIds]);

  // Options for the multi-select — the full roster from the rollup.
  const agentFilterOptions = useMemo(
    () => rollup.map((r) => ({ id: r.agent_id, name: r.agent_name || r.agent_email || "Unknown", role: r.agent_role })),
    [rollup],
  );

  // Iter 170 — when the page loads with `?rep=&snapshot=1`, automatically
  // open the Performance Snapshot panel for that rep as soon as the
  // rollup arrives. Also fetch the per-call rows so Section 3 (duration
  // distribution) and Section 5 (call quality) have data to render.
  useEffect(() => {
    if (!snapshotOpen || !pinnedRepId) return;
    if (loading) return;
    const repRow = rollup.find((r) => r.agent_id === pinnedRepId);
    if (!repRow) return;
    setSnapshotRep(repRow);
    setSnapshotCallsLoading(true);
    (async () => {
      try {
        // Iter 183 (Lisa #f7ad8eb4 Feb 2026) — the snapshot drill-down
        // only honoured date_from/date_to in "custom" mode. When the
        // rep filter was set to "Yesterday" or "Today" the loader fell
        // through to `params.days = days` (e.g. 7), which dragged in
        // today's + recent days' calls and made the panel look wrong.
        // Mirror the same date math the rollup uses for the canned
        // presets so the calls list is always scoped to the visible
        // window.
        //
        // Iter 219.1 (Aug 2026) — LOCAL calendar day (see comment on
        // the sibling helper above at line ~430). Snapshot panel was
        // showing 0 calls for "Today" on US-timezone reps in the
        // evening because this inline `iso()` was using UTC.
        const params = {};
        const iso = (d) =>
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        if (range === "custom" && customFrom && customTo) {
          params.date_from = customFrom;
          params.date_to = customTo;
        } else if (range === "today") {
          const t = new Date();
          params.date_from = iso(t);
          params.date_to = iso(t);
        } else if (range === "yesterday") {
          const y = new Date();
          y.setDate(y.getDate() - 1);
          params.date_from = iso(y);
          params.date_to = iso(y);
        } else {
          params.days = days;
        }
        params.limit = 500;
        Object.assign(params, scopeParams);
        const { data } = await axios.get(`${API}/admin/rep-activity/${pinnedRepId}/calls`, { params, headers: authHeaders() });
        setSnapshotCalls(data?.rows || []);
        setSnapshotCallsTotal(data?.total ?? (data?.summary?.calls || 0));
      } catch (e) {
        toast.error("Couldn't load call detail for the Snapshot");
        setSnapshotCalls([]);
      } finally {
        setSnapshotCallsLoading(false);
      }
    })();
  }, [snapshotOpen, pinnedRepId, rollup, loading, days, range, customFrom, customTo]);

  // Human-readable label for the panel header.
  const rangeLabel = useMemo(() => {
    if (range === "today") return "Today";
    if (range === "yesterday") return "Yesterday";
    if (range === "custom") return customFrom && customTo ? `${customFrom} → ${customTo}` : "Custom range";
    return `Last ${days} day${days === 1 ? "" : "s"}`;
  }, [range, days, customFrom, customTo]);

  // Iter 188.34 — Export the currently-visible rep performance rollup
  // (already filtered by search + pinnedRepId) to a CSV. Columns mirror
  // the on-screen table so managers can reconcile row-by-row.
  const exportOverallCSV = useCallback(() => {
    if (!filteredRollup.length) return;
    const headers = [
      "Rep", "Calls", "Dial attempts", "Unique leads called", "Connects", "DM contacts",
      "Human connect rate", "Avg live conv", "Avg time / lead",
      "Avg call time / day", "Demos Scheduled", "Demos Completed", "SMS",
      "Voicemails", "Avg attempts / advance",
    ];
    const rows = filteredRollup.map((r) => [
      r.agent_name || r.agent_email || "Unassigned",
      r.merged_calls ?? r.calls ?? 0,
      r.calls ?? 0,
      r.unique_leads_called ?? 0,
      r.connects ?? 0,
      r.decision_maker_contacts ?? 0,
      r.true_human_connect_rate == null ? "—" : `${r.true_human_connect_rate}%`,
      fmtSecs(r.avg_talk_sec),
      fmtSecs(r.avg_time_per_lead_sec),
      fmtSecs(r.avg_call_time_per_day_sec),
      r.demos_scheduled ?? 0,
      r.demos_completed ?? 0,
      r.sms ?? 0,
      r.voicemails ?? 0,
      r.stage_closes ? Number(r.avg_attempts_per_stage_advance || 0).toFixed(1) : "—",
    ]);
    const [outHeaders, outRows] = isEhi
      ? _ehiCsv(headers, rows, ["Demos Scheduled", "Demos Completed", "Avg attempts / advance"],
          { "Unique leads called": "Unique clients called", "Avg time / lead": "Avg time / client" })
      : [headers, rows];
    downloadCSV(`${isEhi ? "ehi-" : ""}rep-activity-overall_${todayStamp()}.csv`, outHeaders, outRows);
    toast.success(`Exported ${rows.length} rep${rows.length === 1 ? "" : "s"} to CSV`);
  }, [filteredRollup, rangeLabel, isEhi]);


  const closeSnapshot = () => {
    setSnapshotOpen(false);
    // Iter 170 fix (testing agent iter 124) — keep snapshotRep populated
    // when the panel closes so the snapshot-pinned-banner can render with
    // the rep's name + reopen affordance. Only clear it when the user
    // explicitly hits "Show whole team" or navigates away.
    setSnapshotCalls([]);
    // Clear `snapshot=1` from URL but keep rep + range so a refresh still
    // shows the Activity Detail for this rep.
    const params = new URLSearchParams(location.search);
    params.delete("snapshot");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const viewFullActivity = () => {
    setSnapshotOpen(false);
    // Auto-expand the rep's activity detail panel (toggleAgentRow).
    if (pinnedRepId && expandedAgentId !== pinnedRepId) {
      toggleAgentRow(pinnedRepId);
    }
    const params = new URLSearchParams(location.search);
    params.delete("snapshot");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const reopenSnapshot = () => {
    if (!pinnedRepId) return;
    setSnapshotOpen(true);
    const params = new URLSearchParams(location.search);
    params.set("snapshot", "1");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Iter 171.B (Danny — Performance Snapshot access from Rep Activity table)
  // Opens the slide-out PerformanceSnapshotPanel for the given rep row.
  // The existing snapshot effect (line 256) handles call-detail fetching
  // when `snapshotOpen=true` and `pinnedRepId` is set, so we just point
  // both at the chosen rep and the effect does the rest.
  const openSnapshotForRep = (r, ev) => {
    if (!r?.agent_id) return;
    if (ev) ev.stopPropagation(); // don't toggle the row expansion
    setPinnedRepId(r.agent_id);
    setSnapshotRep(r);
    setSnapshotOpen(true);
    const params = new URLSearchParams(location.search);
    params.set("rep", r.agent_id);
    params.set("snapshot", "1");
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  return (
    <div data-testid="admin-rep-activity-page" style={{ padding: embedded ? "0 22px" : 24, maxWidth: 1400, margin: "0 auto", fontFamily: "inherit" }}>
      {/* Iter 170 — breadcrumb back to Dashboard when arrived via Snapshot link. */}
      {cameFromDashboard && (
        <button
          data-testid="snapshot-back-to-dashboard"
          onClick={() => navigate("/")}
          style={{
            background: "none", border: "none", color: "#2563eb", fontSize: 12, cursor: "pointer",
            fontFamily: "var(--font)", padding: 0, marginBottom: 10, display: "inline-flex", alignItems: "center", gap: 4,
          }}
        >
          <ArrowLeft style={{ width: 12, height: 12 }} /> Back to Dashboard
        </button>
      )}
      {/* Iter 170 — pinned-rep banner with reopen-snapshot affordance.
          Iter 177.9 (Lisa #579a211a Feb 2026) — bumped from a subtle
          lavender chip to a prominent sticky strip with a primary
          "↺ Reopen Snapshot" CTA. Lisa kept getting "lost" after
          clicking into a KPI because the banner was too easy to
          miss — now it sticks to the top of the scroll container
          and uses an action-blue button so the path back is
          unmistakable. */}
      {pinnedRepId && snapshotRep && (
        <div data-testid="snapshot-pinned-banner" style={{
          position: "sticky", top: 0, zIndex: 30,
          padding: "10px 14px", background: "#3730a3",
          color: "#fff", borderRadius: 8, marginBottom: 12,
          fontSize: 12, display: "flex", justifyContent: "space-between",
          alignItems: "center", boxShadow: "0 2px 8px rgba(55,48,163,0.18)",
        }}>
          <span>
            <strong>Snapshot pinned:</strong>&nbsp;{snapshotRep.agent_name || snapshotRep.agent_email} · <em>{rangeLabel}</em>
            {snapshotOpen ? "" : " — drilled into details"}
          </span>
          <span style={{ display: "flex", gap: 8 }}>
            {!snapshotOpen && (
              <button data-testid="snapshot-reopen" onClick={reopenSnapshot} style={{
                background: "#fff", border: "none", color: "#3730a3",
                borderRadius: 6, padding: "5px 12px", fontSize: 11,
                fontWeight: 700, cursor: "pointer", fontFamily: "var(--font)",
                display: "inline-flex", alignItems: "center", gap: 4,
              }}>
                ↺ Reopen Snapshot
              </button>
            )}
            <button data-testid="snapshot-clear-pin" onClick={() => { setPinnedRepId(null); setSnapshotRep(null); setSnapshotCalls([]); navigate(location.pathname, { replace: true }); }} style={{
              background: "transparent", border: "0.5px solid rgba(255,255,255,0.4)",
              color: "#fff", fontSize: 11, cursor: "pointer", fontFamily: "var(--font)",
              borderRadius: 6, padding: "5px 10px",
            }}>
              Show whole team
            </button>
          </span>
        </div>
      )}
      {/* Iter 179.19 — Honor Rate card for the pinned rep. Silently
          hides (returns null) if the endpoint 404s or the current
          user lacks manager scope, so it never breaks the surrounding
          Rep Activity page. */}
      {pinnedRepId && (
        <div style={{ marginBottom: 12, display: "flex", gap: 12, flexWrap: "wrap" }}
             data-testid="honor-rate-card-slot">
          <HonorRateCard
            userId={pinnedRepId}
            since={customFrom || undefined}
            until={customTo || undefined}
          />
        </div>
      )}
      <RcReconnectBanner />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4 }}>Admin · oversight</div>
          <h1 data-testid="rep-activity-title" style={{ fontSize: 26, fontWeight: 700, color: "#0f172a", letterSpacing: "-0.02em" }}>{isEhi ? "EHI Client Rep Activity" : "Rep Activity Report"}</h1>
          <div data-testid="rep-activity-subtitle" style={{ fontSize: 13, color: "#64748b", marginTop: 2 }}>
            {isEhi
              ? "Calls, talk time and call drill-down for EHI clients across the team."
              : "Calls, talk time, sentiment, demos, transcript drill-down, and AI spend across the team."}
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Iter 167 (Bug #6 — Danny report): added Yesterday preset
              + Custom range date pickers. */}
          <select
            data-testid="rep-activity-window"
            value={range}
            onChange={(e) => {
              const v = e.target.value;
              setRange(v);
              if (v === "today") setDays(0);
              else if (v === "7" || v === "30" || v === "90") setDays(Number(v));
            }}
            style={{
              padding: "6px 10px", fontSize: 12, border: "0.5px solid #e2e8f0", borderRadius: 8, fontFamily: "inherit",
            }}>
            {/* Iter 147 — "Today" = calendar day since 00:00 UTC (sentinel
                value 0 on the backend), not a rolling 24-hour window. */}
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="custom">Custom range…</option>
          </select>
          {range === "custom" && (
            <>
              <input
                type="date"
                data-testid="rep-activity-custom-from"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
                style={{ padding: "6px 8px", fontSize: 12, border: "0.5px solid #e2e8f0", borderRadius: 8, fontFamily: "inherit" }}
              />
              <span style={{ fontSize: 11, color: "#64748b" }}>to</span>
              <input
                type="date"
                data-testid="rep-activity-custom-to"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
                style={{ padding: "6px 8px", fontSize: 12, border: "0.5px solid #e2e8f0", borderRadius: 8, fontFamily: "inherit" }}
              />
            </>
          )}
          <div style={{ position: "relative" }}>
            <Search style={{ width: 13, height: 13, position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
            <input
              data-testid="rep-activity-search"
              placeholder="Search agent…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 26, padding: "6px 10px 6px 26px", fontSize: 12, border: "0.5px solid #e2e8f0", borderRadius: 8, fontFamily: "inherit" }}
            />
          </div>
          {isManager && (
            <AgentMultiSelect
              options={agentFilterOptions}
              selected={selectedAgentIds}
              onChange={setSelectedAgentIds}
              testidPrefix="rep-activity-agent"
            />
          )}
          {/* Iter 188.34 — Contextual Export CSV for the overall
              rep-performance rollup currently visible in the table.
              Honours the search + date-range filter so managers can
              slice-and-export the exact roster they see. */}
          <button
            data-testid="rep-activity-export-overall"
            onClick={exportOverallCSV}
            disabled={filteredRollup.length === 0}
            title={filteredRollup.length === 0 ? "No rep activity to export" : `Export ${filteredRollup.length} rep${filteredRollup.length === 1 ? "" : "s"} to CSV`}
            style={{
              padding: "6px 12px", fontSize: 12, border: "0.5px solid #cbd5e1",
              borderRadius: 8, fontFamily: "inherit", cursor: filteredRollup.length === 0 ? "not-allowed" : "pointer",
              background: filteredRollup.length === 0 ? "#f1f5f9" : "#fff",
              color: filteredRollup.length === 0 ? "#94a3b8" : "#0f172a",
              display: "inline-flex", alignItems: "center", gap: 5, fontWeight: 600,
            }}
          >
            <FileText style={{ width: 12, height: 12 }} /> Export CSV
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 14, borderBottom: "0.5px solid #e2e8f0" }}>
        {[
          // Iter 179.12 — rename for clarity. Reps see only the rollup
          // tab; compliance + AI spend are manager-only.
          { id: "rollup", label: isManager ? "Rep performance" : "Your activity", icon: TrendingUp, count: rollup.length },
          ...(isManager && !isEhi ? [
            { id: "compliance", label: "Compliance review", icon: AlertTriangle, count: compliance.length },
            { id: "usage", label: "AI spend", icon: DollarSign, count: fmtUSD(usage.total_cost_usd) },
          ] : []),
        ].map((t) => (
          <button key={t.id} data-testid={`rep-activity-tab-${t.id}`} onClick={() => setTab(t.id)} style={{
            padding: "8px 14px", fontSize: 12, fontWeight: 600, background: "transparent",
            color: tab === t.id ? "#0f172a" : "#64748b",
            border: 0, borderBottom: tab === t.id ? "2px solid #5B6CFF" : "2px solid transparent",
            cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit",
          }}>
            <t.icon style={{ width: 13, height: 13 }} />
            {t.label}
            <span style={{ background: tab === t.id ? "#5B6CFF" : "#e2e8f0", color: tab === t.id ? "#fff" : "#475569", fontSize: 10, padding: "1px 7px", borderRadius: 999, fontWeight: 700 }}>
              {t.count}
            </span>
          </button>
        ))}
      </div>

      {loading && <div style={{ padding: 40, textAlign: "center", color: "#64748b" }}><Loader2 style={{ width: 18, height: 18, animation: "spin 1s linear infinite" }} /></div>}

      {!loading && tab === "rollup" && (
        <div data-testid="rollup-table" style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ background: "#f8fafc", color: "#475569", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 10 }}>
                <th style={{ padding: "10px 14px", textAlign: "left", width: 22 }}></th>
                {(isEhi
                  ? ["Rep", "Calls", "Unique clients called", "Connects", "DM contacts", "Human connect rate", "Avg live conv", "Avg time / client", "Avg call time / day", "SMS", "Voicemails"]
                  : ["Rep", "Calls", "Unique leads called", "Connects", "DM contacts", "Human connect rate", "Avg live conv", "Avg time / lead", "Avg call time / day", "Demos Scheduled", "Demos Completed", "SMS", "Voicemails", "Avg attempts / advance"]
                ).map((h) => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: h === "Rep" ? "left" : "right" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredRollup.length === 0 ? (
                <tr><td data-testid="rollup-empty" colSpan={isEhi ? 12 : 15} style={{ padding: 28, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>{isEhi ? "No EHI client calls in this window." : "No rep activity in this window."}</td></tr>
              ) : filteredRollup.map((r) => {
                const aid = r.agent_id || "unassigned";
                const isExpanded = expandedAgentId === aid;
                return (
                  <React.Fragment key={aid}>
                    <tr
                      data-testid={`rollup-row-${aid}`}
                      onClick={() => toggleAgentRow(r.agent_id)}
                      style={{
                        borderTop: "0.5px solid #e2e8f0",
                        cursor: r.agent_id ? "pointer" : "default",
                        background: isExpanded ? "#f1f5f9" : "transparent",
                      }}
                      onMouseOver={(e) => { if (r.agent_id && !isExpanded) e.currentTarget.style.background = "#f8fafc"; }}
                      onMouseOut={(e) => { if (!isExpanded) e.currentTarget.style.background = "transparent"; }}
                    >
                      <td style={{ padding: "10px 14px", width: 22 }}>
                        {r.agent_id && (isExpanded
                          ? <ChevronDown style={{ width: 13, height: 13, color: "#5B6CFF" }} />
                          : <ChevronRight style={{ width: 13, height: 13, color: "#94a3b8" }} />)}
                      </td>
                      <td style={{ padding: "10px 14px" }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: "#0f172a" }}>{r.agent_name || r.agent_email || "Unassigned"}</div>
                          {r.agent_email && r.agent_name !== r.agent_email && <div style={{ fontSize: 10, color: "#94a3b8" }}>{r.agent_email}</div>}
                          {r.agent_id && (
                            <div style={{ display: "inline-flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                              <button
                                data-testid={`rollup-open-snapshot-${r.agent_id}`}
                                onClick={(e) => openSnapshotForRep(r, e)}
                                title={`Open Performance Snapshot for ${r.agent_name || r.agent_email}`}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "3px 8px", borderRadius: 12, fontSize: 10, fontWeight: 600,
                                  background: "#eef2ff", color: "#3730a3", border: "0.5px solid #c7d2fe",
                                  cursor: "pointer", fontFamily: "inherit",
                                  transition: "background-color 120ms ease",
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.background = "#e0e7ff"; }}
                                onMouseOut={(e) => { e.currentTarget.style.background = "#eef2ff"; }}
                              >
                                <BarChart3 style={{ width: 11, height: 11 }} />
                                Snapshot
                              </button>
                              {/* Iter 193 (Vipal — CCM Coaching Agent):
                                  one-click Lumina coaching report for
                                  the rep over the same range. */}
                              <button
                                data-testid={`rollup-open-coaching-${r.agent_id}`}
                                onClick={(e) => { e.stopPropagation(); setCoachingRep(r); setCoachingOpen(true); }}
                                title={`AI coaching report for ${r.agent_name || r.agent_email}`}
                                style={{
                                  display: "inline-flex", alignItems: "center", gap: 4,
                                  padding: "3px 8px", borderRadius: 12, fontSize: 10, fontWeight: 600,
                                  background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                                  color: "#fff", border: "none",
                                  cursor: "pointer", fontFamily: "inherit",
                                  transition: "filter 120ms ease",
                                }}
                                onMouseOver={(e) => { e.currentTarget.style.filter = "brightness(1.08)"; }}
                                onMouseOut={(e) => { e.currentTarget.style.filter = "none"; }}
                              >
                                ✦ Coach
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td data-testid={`rollup-calls-${r.agent_id}`} title={`Merged calls — retries and duplicates of the same call count once (${r.calls || 0} dial attempts)`} style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", cursor: "help" }}>{r.merged_calls ?? r.calls}</td>
                      {/* Iter 210.2 (Feb 2026 · April "same-lead retry inflates calls" fix).
                          Unique leads contacted — deduped by lead_id via
                          server-side $addToSet. Independent of retry
                          collapse: reflects distinct dial targets, not
                          call volume. */}
                      <td
                        data-testid={`rollup-unique-leads-${aid}`}
                        style={{
                          padding: "10px 14px", textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          color: (r.unique_leads_called || 0) > 0 ? "#0f172a" : "#94a3b8",
                          fontWeight: 500,
                        }}
                        title={
                          (r.calls || 0) > 0
                            ? `${r.unique_leads_called || 0} distinct ${isEhi ? "client" : "lead"}${r.unique_leads_called === 1 ? "" : "s"} dialed. Avg ${((r.calls || 0) / Math.max(r.unique_leads_called || 1, 1)).toFixed(1)} calls per ${isEhi ? "client" : "lead"}. Independent of retry-collapse in the drill-down.`
                            : "No calls in window"
                        }
                      >
                        {r.unique_leads_called || 0}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.connects}</td>
                      <td
                        data-testid={`rollup-dm-contacts-${aid}`}
                        style={{
                          padding: "10px 14px", textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          color: (r.decision_maker_contacts || 0) > 0 ? "#0F6E56" : "#94a3b8",
                          fontWeight: (r.decision_maker_contacts || 0) > 0 ? 600 : 400,
                        }}
                        title={
                          (r.decision_maker_contacts || 0) > 0
                            ? `${r.decision_maker_contacts} decision-maker contact${r.decision_maker_contacts === 1 ? "" : "s"} (${r.decision_maker_rate || 0}% of calls). Breakdown: ${(r.role_breakdown?.physician || 0)} physician · ${(r.role_breakdown?.office_manager || 0)} office mgr · ${(r.role_breakdown?.owner_partner || 0)} owner/partner`
                            : "No decision-maker contacts tagged yet"
                        }
                      >
                        {r.decision_maker_contacts || 0}
                        {(r.decision_maker_contacts || 0) > 0 && (
                          <span style={{ fontSize: 10, color: "#0F6E56", marginLeft: 4 }}>· {r.decision_maker_rate || 0}%</span>
                        )}
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                        <ConnectRateCell row={r} />
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtSecs(r.avg_talk_sec)}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#475569" }} title={`Avg talk time across all ${r.unique_leads_called || 0} unique ${isEhi ? "clients" : "leads"}`}>{fmtSecs(r.avg_time_per_lead_sec)}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#475569" }} title={`Total talk time / ${r.active_days || 0} active day${r.active_days === 1 ? "" : "s"}`}>{fmtSecs(r.avg_call_time_per_day_sec)}</td>
                      {!isEhi && (<>
                      <td
                        style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: (r.demos_scheduled || 0) > 0 ? "#0891b2" : undefined, fontWeight: (r.demos_scheduled || 0) > 0 ? 600 : 400, cursor: "help" }}
                        title={`Meetings booked by ${r.agent_name || r.agent_email} in-window (organizer_user_id).\nExcludes cancels. No-shows still count — the rep did the booking work.\nSource: db.meetings.`}
                        data-testid={`rollup-demos-scheduled-${aid}`}
                      >
                        {(r.demos_scheduled || 0).toLocaleString()}
                      </td>
                      <td
                        style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: (r.demos_completed || 0) > 0 ? "#15803d" : undefined, fontWeight: (r.demos_completed || 0) > 0 ? 600 : 400, cursor: "help" }}
                        title={`Meetings whose status flipped to "completed" in-window.\nShow rate: ${(r.demos_scheduled || 0) > 0 ? Math.round((r.demos_completed || 0) / r.demos_scheduled * 100) : 0}% (${r.demos_completed || 0}/${r.demos_scheduled || 0}).\nSource: db.meetings.status_history[].`}
                        data-testid={`rollup-demos-completed-${aid}`}
                      >
                        {(r.demos_completed || 0).toLocaleString()}
                      </td>
                      </>)}
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.sms}</td>
                      <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.voicemails}</td>
                      {!isEhi && <td
                        style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: "#475569" }}
                        title={r.stage_closes ? `${r.stage_closes} stage advance${r.stage_closes === 1 ? "" : "s"} · channel breakdown: ${r.stage_attempts_by_channel?.call || 0} call / ${r.stage_attempts_by_channel?.email || 0} email / ${r.stage_attempts_by_channel?.sms || 0} sms` : "No stage advances in this window"}
                        data-testid={`rollup-avg-attempts-${aid}`}
                      >
                        {r.stage_closes ? r.avg_attempts_per_stage_advance.toFixed(1) : "—"}
                      </td>}
                    </tr>
                    {isExpanded && (
                      <tr data-testid={`rollup-detail-${aid}`} style={{ background: "#f8fafc" }}>
                        <td colSpan={isEhi ? 12 : 15} style={{ padding: "12px 18px", borderTop: "0.5px solid #e2e8f0" }}>
                          <RepDetailPanel isEhi={isEhi} rollupCalls={r.calls} detail={agentDetail} agentId={r.agent_id} agentName={r.agent_name || r.agent_email} trueHumanConnectRate={r.true_human_connect_rate} trueHumanYes={r.true_human_yes} trueHumanClassified={r.true_human_classified} days={days} rangeLabel={rangeLabel} page={agentDetailPage} pageSize={AGENT_DETAIL_PAGE_SIZE} onPageChange={goToAgentDetailPage} onExportCSV={exportRepDetailCSV} onOpenTranscript={openTranscript_} onOpenCallDetail={(row) => openCallDetail(row, r.agent_id)} token={token} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          {transcripts.length > 0 && (
            <div style={{ borderTop: "0.5px solid #e2e8f0", padding: 14, background: "#f8fafc" }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                Recent call transcripts ({transcripts.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {transcripts.slice(0, 10).map((t, idx) => (
                  <button key={`${t.external_id || "tx"}-${idx}`} data-testid={`transcript-row-${t.external_id}`} onClick={() => openTranscript_(t.external_id)} style={{
                    padding: "8px 10px", fontSize: 11, background: "#fff", border: "0.5px solid #e2e8f0",
                    borderRadius: 6, cursor: "pointer", textAlign: "left",
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, fontFamily: "inherit",
                  }}>
                    <div>
                      <strong>{t.agent_email || t.agent_id?.slice(0, 8) || "—"}</strong>
                      <span style={{ color: "#94a3b8", margin: "0 6px" }}>·</span>
                      {fmtSecs(t.duration_sec)} · sentiment {t.sentiment_final}
                    </div>
                    <ChevronRight style={{ width: 12, height: 12, color: "#94a3b8" }} />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {!loading && tab === "compliance" && (
        <div data-testid="compliance-table" style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 10 }}>
          {compliance.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>No flagged calls in this window. 🎉</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead><tr style={{ background: "#fef3c7", color: "#78350f", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                {["Reason", "Rep", "Lead", "Duration", "Outcome", "When", ""].map((h) => <th key={h} style={{ padding: "10px 14px", textAlign: "left" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {compliance.map((c, i) => (
                  <tr key={i} data-testid={`compliance-row-${i}`} style={{ borderTop: "0.5px solid #e2e8f0" }}>
                    <td style={{ padding: "10px 14px" }}>
                      <span style={{
                        background: c.reason === "negative_sentiment" ? "#fee2e2" : "#fef3c7",
                        color: c.reason === "negative_sentiment" ? "#991b1b" : "#78350f",
                        fontSize: 10, padding: "2px 7px", borderRadius: 999, fontWeight: 700, textTransform: "uppercase",
                      }}>{c.reason.replace("_", " ")}</span>
                    </td>
                    <td style={{ padding: "10px 14px", color: "#0f172a" }}>{c.agent_name || c.agent_email || c.agent_id?.slice(0, 8) || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#475569" }}>{c.lead_name || c.lead_id?.slice(0, 8) || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#475569" }}>{fmtSecs(c.duration_sec)}</td>
                    <td style={{ padding: "10px 14px", color: "#475569" }}>{c.outcome || c.sentiment_final || "—"}</td>
                    <td style={{ padding: "10px 14px", color: "#94a3b8", fontSize: 11 }}>{c.at ? new Date(c.at).toLocaleString() : ""}</td>
                    <td style={{ padding: "10px 14px" }}>
                      <button data-testid={`compliance-open-${i}`} onClick={() => openTranscript_(c.external_id)} style={{
                        padding: "4px 8px", fontSize: 10, fontWeight: 600, background: "#fff", color: "#5B6CFF",
                        border: "0.5px solid #c7d2fe", borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
                      }}>Review</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {!loading && tab === "usage" && (
        <div data-testid="usage-table" style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 10, overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", background: "#eff6ff", borderBottom: "0.5px solid #bfdbfe", display: "flex", alignItems: "center", gap: 10 }}>
            <DollarSign style={{ width: 16, height: 16, color: "#2563eb" }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e3a8a" }}>
              Total AI spend (last {days}d): {fmtUSD(usage.total_cost_usd)}
            </div>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead><tr style={{ background: "#f8fafc", color: "#475569", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {["Rep", "Endpoint", "Provider", "Calls", "Audio (s)", "In tok", "Out tok", "Cost"].map((h) => (
                <th key={h} style={{ padding: "10px 14px", textAlign: h === "Rep" || h === "Endpoint" || h === "Provider" ? "left" : "right" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {usage.rows.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: 28, textAlign: "center", color: "#94a3b8", fontStyle: "italic" }}>No AI usage logged in this window.</td></tr>
              ) : usage.rows.map((r, i) => (
                <tr key={i} data-testid={`usage-row-${i}`} style={{ borderTop: "0.5px solid #e2e8f0" }}>
                  <td style={{ padding: "10px 14px", color: "#0f172a" }}>{r.user_email || r.user_id?.slice(0, 8) || "—"}</td>
                  <td style={{ padding: "10px 14px", color: "#475569", fontFamily: "ui-monospace, monospace", fontSize: 11 }}>{r.endpoint}</td>
                  <td style={{ padding: "10px 14px", color: "#475569" }}>{r.provider}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.calls}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.audio_seconds || "—"}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.input_tokens || "—"}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.output_tokens || "—"}</td>
                  <td style={{ padding: "10px 14px", textAlign: "right", fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{fmtUSD(r.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Iter 141 — Per-call disposition lightbox */}
      {callDetail && callDetail.open && (
        <CallDetailLightbox
          isEhi={isEhi}
          state={callDetail}
          aiSummary={callAISummary}
          onClose={() => { setCallDetail(null); setCallAISummary(null); }}
          onRunAI={runCallAISummary}
          onOpenTranscript={openTranscript_}
          apiBase={API}
          authHeaders={authHeaders}
        />
      )}

      {/* Transcript modal */}
      {openTranscriptId && (
        <div data-testid="transcript-modal" style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,0.6)", backdropFilter: "blur(4px)",
          zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={(e) => e.target === e.currentTarget && setOpenTranscriptId(null)}>
          <div style={{ background: "#fff", borderRadius: 12, maxWidth: 720, width: "100%", maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "14px 18px", borderBottom: "0.5px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Activity style={{ width: 15, height: 15, color: "#5B6CFF" }} />
                <div style={{ fontSize: 14, fontWeight: 600 }}>Call transcript</div>
              </div>
              <button data-testid="transcript-close" onClick={() => setOpenTranscriptId(null)} style={{ padding: 6, background: "transparent", border: 0, cursor: "pointer", color: "#64748b" }}>
                <X style={{ width: 16, height: 16 }} />
              </button>
            </div>
            <div style={{ padding: 16, overflowY: "auto" }}>
              {!openTranscript ? (
                <div style={{ textAlign: "center", padding: 30, color: "#94a3b8" }}><Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite" }} /></div>
              ) : (
                <>
                  <div style={{ fontSize: 11, color: "#64748b", marginBottom: 10, display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <span><strong>Rep:</strong> {openTranscript.agent_email}</span>
                    <span>·</span>
                    <span><strong>Duration:</strong> {fmtSecs(openTranscript.duration_sec)}</span>
                    <span>·</span>
                    <span><strong>Final sentiment:</strong> {openTranscript.sentiment_final}</span>
                  </div>
                  {openTranscript.coach_summary && (
                    <div style={{ background: "#f0f9ff", border: "0.5px solid #bae6fd", borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 11, color: "#0c4a6e" }}>
                      <strong>AI coach summary:</strong> {openTranscript.coach_summary}
                    </div>
                  )}
                  <div style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace", fontSize: 12, lineHeight: 1.55 }}>
                    {(openTranscript.transcript || []).map((t, i) => (
                      <div key={i} style={{ marginBottom: 6 }}>
                        <strong style={{ color: t.speaker === "prospect" ? "#9333ea" : "#0369a1" }}>{(t.speaker || "rep").toUpperCase()}:</strong> {t.text}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>

      {/* Iter 170 — Performance Snapshot slide-out panel. */}
      <PerformanceSnapshotPanel
        open={snapshotOpen && !!snapshotRep}
        rep={snapshotRep}
        calls={snapshotCalls}
        callsLoading={snapshotCallsLoading}
        callsTotal={snapshotCallsTotal}
        rangeLabel={rangeLabel}
        onClose={closeSnapshot}
        onViewFullActivity={viewFullActivity}
        onOpenCallReview={(call) => snapshotRep && openCallDetail(call, snapshotRep.agent_id)}
      />

      {/* Iter 193 (Vipal — CCM Coaching Agent): Lumina coaching report drawer. */}
      <CoachingDrawer
        open={coachingOpen && !!coachingRep}
        rep={coachingRep}
        rangeDays={Number(range) || 7}
        onClose={() => { setCoachingOpen(false); setCoachingRep(null); }}
      />
    </div>
  );
}

/* ── Iter 170.6 (Josh — Connect Rate breakdown 2026-06-05) ──────
   Click-to-open popover that breaks the headline connect_rate into
   its constituent outcomes (connected · voicemail · no_answer · busy
   · wrong_number · callback · not_interested · other). Hidden by
   default so the table stays scannable; reveals on click.
*/
const CONNECT_BREAKDOWN_LABELS = [
  { id: "connected",      label: "Connected",      color: "#059669", bg: "#ecfdf5", counts: true },
  { id: "callback",       label: "Callback set",   color: "#0284c7", bg: "#e0f2fe", counts: false },
  { id: "voicemail",      label: "Voicemail",      color: "#d97706", bg: "#fffbeb", counts: false },
  { id: "no_answer",      label: "No answer",      color: "#64748b", bg: "#f1f5f9", counts: false },
  { id: "busy",           label: "Busy",           color: "#64748b", bg: "#f1f5f9", counts: false },
  { id: "wrong_number",   label: "Wrong / disc.",  color: "#9a3412", bg: "#fff7ed", counts: false },
  { id: "not_interested", label: "Not interested", color: "#7c3aed", bg: "#f5f3ff", counts: false },
  { id: "other",          label: "Other",          color: "#64748b", bg: "#f1f5f9", counts: false },
];

function ConnectRateCell({ row }) {
  const [open, setOpen] = useState(false);
  const breakdown = row.outcome_breakdown || {};
  const total = row.calls || 0;
  // Iter 322.3 — this column now displays the AI-classified True Human
  // Connect rate (human ÷ classified; unclassified excluded). The older
  // outcome-based rate (row.connect_rate) is preserved inside the popover.
  const thc = (row.true_human_connect_rate === undefined) ? null : row.true_human_connect_rate;
  const thcYes = row.true_human_yes || 0;
  const thcClassified = row.true_human_classified || 0;
  // Denominator = all of the rep's calls in the window.
  const thcTotal = row.calls || 0;
  // Never red: any non-zero human connect rate is green; a true 0 reads
  // neutral black; no calls at all stays grey ("—").
  const thcColor = thc == null ? "#94a3b8" : (thc === 0 ? "#0f172a" : "#0F6E56");
  const thcLabel = thc == null ? "—" : `${thc}%`;
  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        data-testid={`connect-rate-toggle-${row.agent_id}`}
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title={thc == null ? "No calls in this window" : `${thcYes} live human of ${thcTotal} dial attempt${thcTotal === 1 ? "" : "s"}`}
        style={{
          background: "transparent", border: 0, padding: 0, cursor: "pointer",
          font: "inherit", color: thcColor, fontWeight: 600, fontVariantNumeric: "tabular-nums",
          borderBottom: "1px dotted currentColor",
        }}
      >
        {thcLabel}
      </button>
      {open && (
        <>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", inset: 0, zIndex: 40, background: "transparent" }}
          />
          <div
            data-testid={`connect-rate-popover-${row.agent_id}`}
            style={{
              position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 50,
              width: 300, background: "#fff",
              border: "0.5px solid #e2e8f0", borderRadius: 10,
              boxShadow: "0 10px 28px rgba(15,23,42,0.14)",
              padding: 12, textAlign: "left", color: "#0f172a",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Section 1 — True Human Connect Rate (headline) ── */}
            <div data-testid={`true-human-section-${row.agent_id}`} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>True Human Connect Rate</div>
                <div style={{ fontSize: 15, fontWeight: 800, color: thcColor, fontVariantNumeric: "tabular-nums" }}>{thcLabel}</div>
              </div>
              {thc == null ? (
                <div style={{ fontSize: 10.5, color: "#64748b", lineHeight: 1.5 }}>
                  No calls in the selected window yet. Once the rep makes calls, this shows the share that were genuine live human conversations.
                </div>
              ) : (
                <div style={{ fontSize: 10.5, color: "#64748b", lineHeight: 1.5 }}>
                  <strong style={{ color: "#0f172a" }}>{thcYes} live human conversation{thcYes === 1 ? "" : "s"} across {thcTotal} dial attempt{thcTotal === 1 ? "" : "s"}.</strong> Rate = live human conversations ÷ all dial attempts. Calls classified as not-human <em>and</em> calls not yet reviewed by the AI both count against the rate, so it reads low but strictly reflects genuine live connects.
                </div>
              )}
            </div>
            {/* ── Section 2 — Outcome-based connect rate (existing) ── */}
            <div style={{ borderTop: "0.5px solid #e2e8f0", paddingTop: 10 }}>
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em" }}>Outcome-based connect rate</div>
                <div style={{ fontSize: 11, color: "#475569" }}>of <strong>{total}</strong> calls</div>
              </div>
              {CONNECT_BREAKDOWN_LABELS.map((b) => {
                const n = breakdown[b.id] || 0;
                const pct = total ? Math.round((n / total) * 1000) / 10 : 0;
                return (
                  <div
                    key={b.id}
                    data-testid={`connect-breakdown-${row.agent_id}-${b.id}`}
                    style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, opacity: n ? 1 : 0.55 }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: 999, background: b.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: "#0f172a", flex: 1 }}>
                      {b.label}
                      {b.counts && <span title="Counts toward connect rate" style={{ marginLeft: 4, fontSize: 9, color: "#059669", fontWeight: 700 }}>✓</span>}
                    </span>
                    <span style={{ fontSize: 11, color: "#475569", fontVariantNumeric: "tabular-nums" }}>{n}</span>
                    <span style={{ fontSize: 10, color: "#94a3b8", minWidth: 36, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
                  </div>
                );
              })}
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: "0.5px dashed #e2e8f0", fontSize: 10, color: "#64748b" }}>
                <strong style={{ color: "#0f172a" }}>{row.connect_rate}%</strong> outcome-based connect rate = Live convos ÷ total calls
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


/* ── Inline drill-down: per-rep call & SMS detail ─────────────── */
function RepDetailPanel({ isEhi = false, rollupCalls, detail, agentId, agentName, trueHumanConnectRate = null, trueHumanYes = 0, trueHumanClassified = 0, days, rangeLabel = "the selected window", page = 1, pageSize = 50, onPageChange, onExportCSV, onOpenTranscript, onOpenCallDetail, token }) {
  // Iter 210.2 — per-cluster expansion state. Keyed by anchor row's
  // id so state survives page-level re-renders as long as the row
  // identity is stable. Independent of the outer agent-expansion
  // state to avoid coupling.
  const [expandedClusters, setExpandedClusters] = useState({});
  const toggleCluster = useCallback((rowKey) => {
    setExpandedClusters((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  }, []);
  if (!detail || detail.agent_id !== agentId) {
    return <div style={{ fontSize: 11, color: "#94a3b8", padding: 8 }}>Loading…</div>;
  }
  if (detail.loading) {
    return (
      <div style={{ padding: 16, textAlign: "center", color: "#64748b" }}>
        <Loader2 style={{ width: 14, height: 14, animation: "spin 1s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
        Loading call history…
      </div>
    );
  }
  if (detail.error) {
    return <div style={{ fontSize: 11, color: "#dc2626", padding: 8 }}>Couldn't load detail. Try again.</div>;
  }
  const rows = detail.rows || [];
  const summary = detail.summary || {};
  if (rows.length === 0) {
    return (
      <div data-testid={`rep-detail-empty-${agentId}`} style={{ padding: 16, textAlign: "center", color: "#94a3b8", fontStyle: "italic", fontSize: 11 }}>
        {isEhi ? "No EHI client calls logged for this rep" : "No calls or SMS logged for this rep"} for {rangeLabel.toLowerCase()}.
      </div>
    );
  }
  const outcomeColor = (o) => {
    if (o === "connected" || o === "demo-booked") return { bg: "#dcfce7", color: "#166534" };
    if (o === "voicemail") return { bg: "#dbeafe", color: "#1e40af" };
    if (o === "no-answer") return { bg: "#f1f5f9", color: "#475569" };
    if (o === "busy") return { bg: "#fef3c7", color: "#854d0e" };
    // Iter 168.f — "left_on_hold" outcome (Ioannis 12e3e658). Uses a
    // distinct warm-amber so admins can spot stuck-on-hold patterns at
    // a glance in the Rep Activity table.
    if (o === "left_on_hold") return { bg: "#fef3c7", color: "#854F0B" };
    return { bg: "#f1f5f9", color: "#475569" };
  };
  const fmtSec = (s) => {
    const m = Math.floor((s || 0) / 60), sec = (s || 0) % 60;
    return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
  };

  return (
    <div data-testid={`rep-detail-${agentId}`}>
      {/* Summary chip strip */}
      <div style={{ display: "flex", gap: 14, marginBottom: 10, fontSize: 11, color: "#475569", flexWrap: "wrap" }}>
        <span data-testid={`rep-detail-dial-attempts-${agentId}`}><strong style={{ color: "#0f172a" }}>{rollupCalls ?? summary.calls ?? 0}</strong> dial attempts</span>
        <span>·</span>
        <span><strong style={{ color: "#166534" }}>{summary.connects || 0}</strong> connects</span>
        <span>·</span>
        <span data-testid={`rep-detail-true-human-${agentId}`} title={trueHumanConnectRate == null ? "No calls in this window" : `${trueHumanYes || 0} live human of ${rollupCalls ?? summary.calls ?? 0} dial attempts`} style={{ cursor: "help" }}>Human connect rate <strong style={{ color: trueHumanConnectRate == null ? "#94a3b8" : (trueHumanConnectRate === 0 ? "#0f172a" : "#0F6E56") }}>{trueHumanConnectRate == null ? "—" : `${trueHumanConnectRate}%`}</strong></span>
        <span>·</span>
        <span>Avg live conv <strong style={{ color: "#0f172a" }}>{(() => {
          const s = summary.avg_live_conversation_sec || 0;
          const m = Math.floor(s / 60), sec = s % 60;
          return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
        })()}</strong></span>
        {(summary.ivr_calls || 0) > 0 && (<><span>·</span><span title="Auto-attendant / IVR calls excluded from connect rate"><strong style={{ color: "#0d9488" }}>{summary.ivr_calls}</strong> IVR</span></>)}
        {(summary.avg_voicemail_sec || 0) > 0 && (<><span>·</span><span title={(summary.avg_voicemail_sec || 0) > 45 ? "Voicemails are too long — keep under 30s" : "Avg voicemail duration"}><strong style={{ color: (summary.avg_voicemail_sec || 0) > 45 ? "#c2410c" : "#0f172a" }}>{(() => {
          const s = summary.avg_voicemail_sec || 0;
          const m = Math.floor(s / 60), sec = s % 60;
          return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
        })()}</strong> avg VM</span></>)}
        <span>·</span>
        <span><strong style={{ color: "#0f172a" }}>{detail.total ?? summary.events ?? rows.length}</strong> total events</span>
        {/* Iter 188.34 — Per-rep contextual Export CSV. Pulls the full
            date-range (not just the current 50-row page) so managers
            can slice-and-audit an individual rep's calls in Excel. */}
        {onExportCSV && (
          <span style={{ marginLeft: "auto" }}>
            <button
              data-testid={`rep-activity-export-rep-${agentId}`}
              onClick={(e) => { e.stopPropagation(); onExportCSV(agentId, agentName); }}
              title={`Export ${agentName || "rep"}'s full call log for ${rangeLabel.toLowerCase()}`}
              style={{
                padding: "4px 10px", fontSize: 11, border: "0.5px solid #cbd5e1",
                borderRadius: 6, fontFamily: "inherit", cursor: "pointer",
                background: "#fff", color: "#0f172a", fontWeight: 600,
                display: "inline-flex", alignItems: "center", gap: 4,
              }}
            >
              <FileText style={{ width: 11, height: 11 }} /> Export CSV
            </button>
          </span>
        )}
      </div>
      <div style={{ background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
          <thead>
            <tr style={{ background: "#f1f5f9", color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>
              {/* Iter 218 (Aug 2026 · Bug — column labeled "Outcome" was
                  actually rendering the LEAD's sub_status as a tiered
                  chip, while the real call outcome was buried in the
                  row's hover tooltip). Column count grew from 11 → 12
                  so admins can see the call outcome AND the sub-status
                  side-by-side without hovering. Screen columns are now
                  1:1 with the CSV export headers below (server.py:9315
                  already surfaces both `outcome` + `lead_sub_status`
                  — no backend change needed).

                  Iter 220 (Aug 2026 · UX cleanup) — removed the standalone
                  "Call ID" column and moved it into the Call Review
                  modal. Also stripped the wall-clock start→end timing
                  block from the "When" column and the recording-ID
                  text from the "Recording" column — both surfaces are
                  now consolidated in the modal's meta strip. Effect:
                  table reads faster at a glance (When = timestamp,
                  Recording = play control), and the "diagnostic"
                  fields still available one click away. */}
              {(isEhi
                ? ["When", "Client", "Phone", "Call outcome", "True Outcome", "Human Connect", "Contact", "Duration", "Recording", "Sentiment", "Notes / summary", ""]
                : ["When", "Lead", "Phone", "Call outcome", "True Outcome", "Human Connect", "Sub-status", "Contact", "Duration", "Recording", "Sentiment", "Notes / summary", ""]
              ).map((h) => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const oc = outcomeColor(row.outcome);
              const hasNotes = (row.agent_notes && row.agent_notes.length > 0) || !!row.coach_summary || !!row.text;
              const firstNote = (row.agent_notes && row.agent_notes[0] && row.agent_notes[0].text) || "";
              // Iter 343 — hover-tooltip preview so admins can quickly
              // scan disposition context without opening the full
              // lightbox. Native `title` attribute for zero perf cost.
              const tooltipParts = [];
              if (row.outcome) tooltipParts.push(`Outcome: ${row.outcome}${row.outcome_detail ? ` (${row.outcome_detail})` : ""}`);
              if (row.call_type) tooltipParts.push(`Call type: ${row.call_type}`);
              if (row.next_step) tooltipParts.push(`Next step: ${row.next_step}`);
              if (Array.isArray(row.product_tags) && row.product_tags.length) tooltipParts.push(`Products: ${row.product_tags.join(", ")}`);
              if (row.substatus) tooltipParts.push(`Sub-status: ${row.substatus}`);
              if (row.duration_sec) tooltipParts.push(`Duration: ${Math.floor(row.duration_sec / 60)}m ${row.duration_sec % 60}s`);
              if (row.notes_preview) tooltipParts.push(`Note: ${row.notes_preview}`);
              const tooltip = tooltipParts.length ? tooltipParts.join("\n") : (row.outcome || "");
              const rowKey = row.id || row.external_id || `row-${i}`;
              const isCluster = row.attempt_count > 1 && Array.isArray(row.attempts_detail) && row.attempts_detail.length > 1;
              const isExpanded = isCluster && !!expandedClusters[rowKey];
              return (
                <React.Fragment key={rowKey}>
                <tr
                  data-testid={`rep-detail-row-${i}`}
                  title={tooltip}
                  onClick={() => row.kind === "call" && onOpenCallDetail && onOpenCallDetail(row)}
                  style={{
                    borderTop: "0.5px solid #f1f5f9",
                    cursor: row.kind === "call" && onOpenCallDetail ? "pointer" : "default",
                    background: isExpanded ? "#f8fafc" : "transparent",
                  }}
                  onMouseOver={(e) => { if (row.kind === "call" && !isExpanded) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseOut={(e) => { if (!isExpanded) e.currentTarget.style.background = "transparent"; }}
                >
                  <td style={{ padding: "8px 10px", color: "#475569" }}>
                    {/* Iter 220 (Aug 2026) — wall-clock detail moved to
                        the Call Review modal (see CallDetailLightbox's
                        meta strip). This cell now shows just the
                        row's canonical timestamp so the table reads
                        cleanly at a glance. Jun 2026 — date on line 1,
                        time stacked beneath so the column stays narrow
                        and the table grows vertically, not sideways. */}
                    {row.at ? (
                      <>
                        <div style={{ fontVariantNumeric: "tabular-nums" }}>{new Date(row.at).toLocaleDateString()}</div>
                        <div style={{ fontSize: 10, color: "#94a3b8", fontVariantNumeric: "tabular-nums" }}>{new Date(row.at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</div>
                      </>
                    ) : (
                      <div>—</div>
                    )}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#0f172a" }}>
                    {row.ehi_client_id ? (
                      <a
                        data-testid={`rep-detail-ehi-link-${row.id}`}
                        href={`/ehi-clients/${row.ehi_client_id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: "#5B6CFF", textDecoration: "none", fontWeight: 600 }}
                      >{row.lead_name}</a>
                    ) : row.lead_id ? (
                      <a
                        href={`/lead-focus/${row.lead_id}`}
                        onClick={(e) => {
                          // Iter 192q (Lisa #5509aa4d) — preserve the exact
                          // Rep Activity URL (filters + tab + drill-down)
                          // so the lead-focus page can render a "Back to
                          // Rep Activity Report" button that returns the
                          // user to the same filtered view they came from.
                          try {
                            sessionStorage.setItem("leadFocusReturn", JSON.stringify({
                              url: window.location.pathname + window.location.search,
                              label: "Rep Activity Report",
                              ts: Date.now(),
                            }));
                          } catch { /* private mode — non-fatal */ }
                          e.stopPropagation();
                        }}
                        style={{ color: "#5B6CFF", textDecoration: "none", fontWeight: 600 }}
                      >{row.lead_name}</a>
                    ) : (
                      <span style={{ color: "#94a3b8" }}>{row.lead_name}</span>
                    )}
                    {row.practice && <div style={{ fontSize: 10, color: "#94a3b8" }}>{row.practice}</div>}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#475569", fontFamily: "ui-monospace, monospace" }}>
                    {row.to_number || row.from_number || "—"}
                    {/* Iter 188.32 · Fix A — Rapid-retry chip. When two
                        or more click-spam retries were merged into this
                        row on the server, surface the count + window
                        so auditors see exactly what was collapsed.
                        Iter 210.2 — chip is now a toggle button that
                        expands an inline sub-table listing each merged
                        attempt with its own timestamp, outcome, and
                        recording. */}
                    {row.attempt_count > 1 && (
                      isCluster ? (
                        <button
                          data-testid={`rep-retry-chip-${i}`}
                          onClick={(e) => { e.stopPropagation(); toggleCluster(rowKey); }}
                          title={`${isExpanded ? "Collapse" : "Expand"} · ${row.attempt_count} merged attempts within ${row.attempt_window_sec}s${row.merge_reason ? ` · merged by ${row.merge_reason.startsWith("same_lead_streak") ? "same-lead streak (5-min)" : row.merge_reason.startsWith("rc_cdr") ? "RC CDR de-dup" : "spam-retry heuristic"}` : ""}\nExternal IDs: ${(row.merged_external_ids || []).join(", ")}`}
                          style={{ display: "inline-flex", alignItems: "center", gap: 3, marginLeft: 6, padding: "1px 7px", background: isExpanded ? "#fcd34d" : "#fef3c7", color: "#92400e", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "none", fontFamily: "system-ui, sans-serif", verticalAlign: "middle", border: "0.5px solid #fbbf24", cursor: "pointer" }}
                        >
                          {isExpanded ? <ChevronDown style={{ width: 9, height: 9 }} /> : <ChevronRight style={{ width: 9, height: 9 }} />}
                          ×{row.attempt_count} in {row.attempt_window_sec}s
                        </button>
                      ) : (
                        <span
                          data-testid={`rep-retry-chip-${i}`}
                          title={`Collapsed ${row.attempt_count} rapid retries within ${row.attempt_window_sec}s (server-side dedupe). External IDs: ${(row.merged_external_ids || []).join(", ")}`}
                          style={{ display: "inline-block", marginLeft: 6, padding: "1px 7px", background: "#fef3c7", color: "#92400e", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "none", fontFamily: "system-ui, sans-serif", verticalAlign: "middle" }}
                        >
                          ×{row.attempt_count} in {row.attempt_window_sec}s
                        </span>
                      )
                    )}
                  </td>
                  <td style={{ padding: "8px 10px" }} data-testid={`rep-detail-outcome-${i}`}>
                    {/* Iter 218 (Aug 2026) — new dedicated Outcome
                        column that always renders the ACTUAL call
                        outcome (row.outcome + row.outcome_detail). The
                        adjacent Sub-status column continues to render
                        the lead's sub_status tier chip. Uses the shared
                        `outcomeLabels.js` vocabulary so the label +
                        color match Lead Focus, the dialer timeline,
                        and every other outcome pill in the app. */}
                    {row.kind === "call" ? (
                      (() => {
                        // Iter 221 (Jun 2026 · Rahul) — "Call outcome" shows the
                        // PHONE-SYSTEM (RingCentral) result. `rc_outcome` is the
                        // raw RC value the backend preserves BEFORE applying the
                        // rep's disposition override; the adjacent "True Outcome"
                        // column shows what the rep actually logged. Falls back
                        // to `row.outcome` for older payloads without rc_outcome.
                        const rawOutcome = String(row.rc_outcome || row.outcome || "").trim();
                        if (!rawOutcome) {
                          return <span style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>;
                        }
                        const label = _canonicalOutcomeLabel(rawOutcome);
                        const chip = outcomeColor(rawOutcome);
                        const detail = null;
                        const tip = `Call outcome: ${label}`;
                        return (
                          <span
                            data-testid={`rep-detail-outcome-chip-${i}`}
                            title={tip}
                            style={{
                              background: chip.bg,
                              color: chip.color,
                              padding: "2px 8px", borderRadius: 8,
                              fontSize: 10, fontWeight: 700,
                              textTransform: "uppercase",
                              maxWidth: 150, display: "inline-block",
                              whiteSpace: "normal", overflowWrap: "normal", wordBreak: "normal", lineHeight: 1.3,
                            }}
                          >
                            {label}
                          </span>
                        );
                      })()
                    ) : (
                      <span style={{ background: "#ede9fe", color: "#6d28d9", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>SMS</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 10px" }} data-testid={`rep-detail-true-outcome-${i}`}>
                    {/* Iter 221 (Jun 2026 · Rahul) — "True Outcome" is the
                        outcome the rep logged on THIS call's after-call-work
                        (disposition) entry. Derived purely from fields the
                        payload already exposes: when the row's outcome came
                        from a disposition (`outcome_source === "disposition"`)
                        we show it; a call that was never dispositioned → "—".
                        The adjacent "Call outcome" column keeps its existing
                        source/behaviour (RC-or-disposition, whichever we have). */}
                    {row.kind === "call" && row.outcome_source === "disposition" && String(row.outcome || "").trim() ? (
                      (() => {
                        const rawOutcome = String(row.outcome || "").trim();
                        const label = _canonicalOutcomeLabel(rawOutcome, row.outcome_detail || undefined);
                        const detail = row.outcome_detail ? String(row.outcome_detail).replace(/_/g, " ") : null;
                        return (
                          <span
                            data-testid={`rep-detail-true-outcome-chip-${i}`}
                            title={detail ? `True outcome: ${label} — ${detail}` : `True outcome: ${label}`}
                            style={{
                              background: oc.bg, color: oc.color,
                              padding: "2px 8px", borderRadius: 8,
                              fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                              maxWidth: 150, display: "inline-block",
                              whiteSpace: "normal", overflowWrap: "normal", wordBreak: "normal", lineHeight: 1.3,
                            }}
                          >
                            {label}
                          </span>
                        );
                      })()
                    ) : (
                      <span data-testid={`rep-detail-true-outcome-empty-${i}`} style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 10px" }} data-testid={`rep-detail-human-connect-${i}`}>
                    {row.kind !== "call" ? (
                      <span style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>
                    ) : row.human_connect_verdict === true ? (
                      <span style={{ background: "#dcfce7", color: "#166534", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>True</span>
                    ) : row.human_connect_verdict === false ? (
                      <span style={{ background: "#fee2e2", color: "#991b1b", padding: "2px 8px", borderRadius: 999, fontSize: 10, fontWeight: 700 }}>False</span>
                    ) : (
                      <span style={{ color: "#94a3b8", fontSize: 10 }} title="Not yet classified">—</span>
                    )}
                  </td>
                  {!isEhi && <td style={{ padding: "8px 10px" }} data-testid={`rep-detail-substatus-${i}`}>
                    {row.kind === "call" ? (() => {
                      // Iter 174.5 (Danny B5 fix). Drop the bare
                      // "CONNECTED" pill entirely:
                      //   • Voicemail/no-answer/busy/failed → legacy pill
                      //   • Connected with a "Contacted — XXX" sub_status
                      //     → tiered pill ("CONNECTED: PHYSICIAN", etc.)
                      //   • Connected WITHOUT a sub_status (auto-promoted
                      //     but rep hasn't tagged role yet) → amber
                      //     "CONNECTED — PENDING TAG" placeholder so the
                      //     rep knows it's awaiting action.
                      // Iter 221 (Jun 2026 · Rahul) — the Sub-status column
                      // now reflects the sub-status recorded on THIS call's
                      // after-call-work (disposition) entry (`row.substatus`),
                      // NOT the lead's global `sub_status` (which was shared
                      // across every row and surfaced a stale default such as
                      // "Quote Sent"). No forced default: a call with no
                      // logged sub-status shows a neutral "—".
                      const sub = row.substatus || "";
                      if (!sub) {
                        return (
                          <span
                            data-testid={`rep-detail-substatus-empty-${i}`}
                            style={{ color: "#cbd5e1", fontSize: 10 }}
                          >
                            —
                          </span>
                        );
                      }
                      const tierForSub = SUB_STATUS_TIER_BY_VALUE[sub];
                      const isContactedSub = /^Contacted —/i.test(sub);
                      const isKnownTier = Boolean(tierForSub) || isContactedSub;
                      const tier = tierForSub || "gatekeeper";
                      const ts = isKnownTier
                        ? (tier === "no_contact" ? NO_CONTACT_STYLE : (TIER_STYLE[tier] || TIER_STYLE.gatekeeper))
                        : { bg: "#f1f5f9", color: "#475569", border: "#e2e8f0" };
                      return (
                        <span
                          data-testid={`rep-outcome-substatus-${i}`}
                          title={sub}
                          style={{
                            background: ts.bg, color: ts.color,
                            border: `0.5px solid ${ts.border}`,
                            padding: "2px 8px", borderRadius: 8,
                            fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                            maxWidth: 150, display: "inline-block",
                            whiteSpace: "normal", overflowWrap: "normal", wordBreak: "normal", lineHeight: 1.3,
                          }}
                        >
                          {subStatusShortLabel(sub)}
                        </span>
                      );
                    })() : (
                      <span style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>
                    )}
                  </td>}
                  {/* Iter 170 — Contact column. Shows the role the rep
                      tagged on the post-call disposition. Empty for
                      non-connected outcomes / SMS rows.
                      Iter 177.5 (Danny #a3eecb5f Feb 2026) — only render
                      the role badge for outcomes that actually reached a
                      human. Otherwise the list view could show
                      "Contacted: Front Desk/Reception" for a call whose
                      Call Review outcome was "No Answer" — that mismatch
                      is what Danny reported (`ALLIED SURGICAL GROUP`).
                      Stale `contact_role` on activity_log rows is real
                      (reps occasionally re-disposition a call) and the
                      authoritative signal for "was anyone reached?" is
                      the outcome, not the role tag. */}
                  <td style={{ padding: "8px 10px" }} data-testid={`rep-detail-contact-${i}`}>
                    {(() => {
                      const HUMAN_OUTCOMES = new Set([
                        "connected", "demo-booked", "demo_booked",
                        "callback", "left_on_hold",
                      ]);
                      const reachedHuman = row.kind === "call" && (
                        row.is_live_human === true || HUMAN_OUTCOMES.has(row.outcome)
                      );
                      if (!(reachedHuman && row.contact_role)) {
                        return <span style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>;
                      }
                      return (
                        <span
                          title={row.is_decision_maker ? "Decision-maker contact" : labelForRole(row.contact_role) || row.contact_role}
                          style={{
                            padding: "1px 7px", borderRadius: 4, fontSize: 10, fontWeight: 600,
                            color: colorForRole(row.contact_role),
                            background: "#f8fafc",
                            border: `0.5px solid ${colorForRole(row.contact_role)}33`,
                            display: "inline-flex", alignItems: "center", gap: 3,
                          }}
                        >
                          {labelForRole(row.contact_role) || row.contact_role}
                          {row.is_decision_maker && (
                            <span style={{ fontSize: 8, fontWeight: 700, color: "#0F6E56", letterSpacing: "0.04em" }}>DM</span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#475569", fontVariantNumeric: "tabular-nums" }}>
                    {row.kind === "call" ? (() => {
                      // Iter 221 (Jun 2026 · Rahul) — RingCentral routinely
                      // under-reports WebPhone call-log duration (0 / a few
                      // seconds) while the recording is minutes long. The old
                      // code showed that few-second value as the PRIMARY
                      // duration with the true length hidden in a side chip.
                      // Now the TRUE recording length (probed from the audio
                      // file, `recording_call_duration`) is the primary value
                      // once known — styled distinctly as a RECORDING length —
                      // and a neutral "unverified" placeholder shows until the
                      // background probe measures it, instead of a fabricated
                      // talk-time.
                      const audioDur = row.recording_call_duration;
                      const sessionDur = row.duration_sec || 0;
                      const hasRecording = Boolean(row.recording_url || row.recording_id);
                      const isPlaceholder =
                        (row.duration_source === "rc_recording_attach" ||
                         row.duration_source === "rc_recording_attach_by_phone") &&
                        (row.duration_was === 0 || row.duration_was === null || row.duration_was === undefined);
                      const untrusted =
                        !audioDur && hasRecording &&
                        (isPlaceholder || row.recording_duration_pending || sessionDur === 0);

                      // 1) True recording length known → primary value.
                      if (audioDur) {
                        const diverges = Math.abs(audioDur - sessionDur) > 3;
                        const tip =
                          `Recording length: ${fmtSec(audioDur)} — measured from the audio file.` +
                          (diverges && sessionDur > 0
                            ? `\n\nRingCentral's call-log duration was only ${fmtSec(sessionDur)}; RC under-reports WebPhone / voicemail calls, so the recording length is the true call length.`
                            : "");
                        return (
                          <span
                            data-testid={`rep-detail-rec-length-${i}`}
                            title={tip}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 4,
                              color: "#5856D6", fontWeight: 600, cursor: "help",
                              borderBottom: diverges ? "1px dashed #c7d2fe" : "none",
                            }}
                          >
                            <span style={{ fontSize: 9 }}>▶</span>{fmtSec(audioDur)}
                          </span>
                        );
                      }

                      // 2) Recording exists but RC gave no trustworthy talk-time
                      //    and we haven't measured the audio yet → "unverified".
                      if (untrusted) {
                        return (
                          <span
                            data-testid={`rep-detail-dur-unverified-${i}`}
                            title={"RingCentral didn't report a reliable talk-time for this call, but a recording exists. The true length is measured from the recording and will appear here within a few minutes. Play the recording to hear the conversation."}
                            style={{ color: "#94a3b8", fontStyle: "italic", cursor: "help" }}
                          >
                            — <span style={{ fontSize: 9, fontStyle: "normal" }}>unverified</span>
                          </span>
                        );
                      }

                      // 3) Trusted RC call-session length (default).
                      const tip = row.duration_flagged
                        ? `Sweeper zeroed a runaway timer.\nOriginal RC-reported value: ${row.duration_was || "?"}s`
                        : "RC call-session length (time between answer & hangup). May include voicemail or hold time.";
                      return (
                        <span
                          title={tip}
                          style={{
                            color: row.duration_flagged ? "#c2410c" : "#475569",
                            fontWeight: row.duration_flagged ? 700 : 400,
                            cursor: "help",
                          }}
                        >
                          {fmtSec(sessionDur)}
                          {row.duration_flagged && (
                            <span data-testid={`rep-detail-dur-flagged-${i}`} style={{ marginLeft: 4, fontSize: 9, color: "#c2410c" }}>⚠</span>
                          )}
                        </span>
                      );
                    })() : "—"}
                  </td>
                  {/* Iter 220 (Aug 2026) — Call ID column removed here
                      and consolidated into the Call Review modal
                      (CallDetailLightbox meta strip). If a manager
                      still needs the RC call ID for a support ticket,
                      it's one click away via the ▶ Review button. */}
                  <td style={{ padding: "8px 10px" }} data-testid={`rep-detail-recording-${i}`}>
                    {row.kind === "call" && (row.recording_id || row.recording_url) ? (
                      <RCRecordingPlayer
                        recordingId={row.recording_id || (row.recording_url || "").match(/\/recording\/(\d+)/)?.[1]}
                        agentId={agentId}
                        token={token}
                        testid={`rep-detail-audio-${i}`}
                      />
                    ) : (
                      <span style={{ color: "#cbd5e1", fontSize: 10 }}>—</span>
                    )}
                  </td>
                  <td style={{ padding: "8px 10px", color: "#475569", textTransform: "capitalize" }}>{row.kind === "call" ? (row.sentiment || "—") : "—"}</td>
                  <td style={{ padding: "8px 10px", color: "#475569", maxWidth: 280, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {firstNote ? (
                      <span title={firstNote}><StickyNote style={{ width: 11, height: 11, verticalAlign: "middle", marginRight: 4, color: "#f59e0b" }} />{firstNote}</span>
                    ) : (
                      row.coach_summary || row.text || (row.stage ? <span style={{ color: "#94a3b8" }}>stage: {row.stage}</span> : "—")
                    )}
                  </td>
                  <td style={{ padding: "8px 10px", display: "flex", gap: 4 }}>
                    {row.kind === "call" && onOpenCallDetail && (
                      <button
                        data-testid={`rep-detail-review-${i}`}
                        onClick={(e) => { e.stopPropagation(); onOpenCallDetail(row); }}
                        style={{ padding: "3px 8px", fontSize: 10, fontWeight: 600, background: hasNotes ? "#fef3c7" : "#fff", color: hasNotes ? "#854d0e" : "#5B6CFF", border: `0.5px solid ${hasNotes ? "#fcd34d" : "#c7d2fe"}`, borderRadius: 6, cursor: "pointer", fontFamily: "inherit", display: "inline-flex", alignItems: "center", gap: 3 }}
                      >
                        <FileText style={{ width: 10, height: 10 }} /> Review
                      </button>
                    )}
                    {row.has_transcript && row.external_id && (
                      <button
                        data-testid={`rep-detail-transcript-${i}`}
                        onClick={(e) => { e.stopPropagation(); onOpenTranscript(row.external_id); }}
                        style={{ padding: "3px 8px", fontSize: 10, fontWeight: 600, background: "#fff", color: "#5B6CFF", border: "0.5px solid #c7d2fe", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}
                      >
                        Transcript
                      </button>
                    )}
                  </td>
                </tr>
                {/* Iter 210.2 · Expandable sub-table for merged clusters.
                    When the user clicks the retry chip we reveal each
                    individual attempt as its own compact row so they
                    can see per-attempt outcome, duration, and play
                    that specific recording. The anchor row above still
                    represents the cluster summary. */}
                {isExpanded && (
                  <tr data-testid={`rep-detail-cluster-expand-${i}`} style={{ background: "#fffbeb", borderTop: "0.5px solid #fcd34d" }}>
                    <td colSpan={isEhi ? 12 : 13} style={{ padding: "0 10px 8px 34px" }}>
                      <div style={{ fontSize: 10, color: "#92400e", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", padding: "6px 0 4px 0" }}>
                        {row.attempt_count} attempts merged
                        {row.merge_reason?.startsWith("same_lead_streak") && " (same-lead streak, 5-min window)"}
                        {row.merge_reason?.startsWith("rc_cdr") && " (RingCentral CDR de-dup)"}
                        {row.attempt_total_duration_sec != null && (
                          <span style={{ marginLeft: 8, color: "#78350f", fontWeight: 500, textTransform: "none" }}>
                            · total time on phone: {fmtSec(row.attempt_total_duration_sec)}
                          </span>
                        )}
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 10, background: "#fff", border: "0.5px solid #fde68a", borderRadius: 6 }}>
                        <thead>
                          <tr style={{ background: "#fef3c7", color: "#78350f", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", fontSize: 9 }}>
                            {["#", "When", "Phone Number", "Outcome", "Duration", "Call ID", "Recording", "Sentiment"].map((h) => (
                              <th key={h} style={{ padding: "5px 8px", textAlign: "left" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {(row.attempts_detail || []).map((att, ai) => {
                            const attOc = outcomeColor(att.outcome);
                            const recId = att.recording_id || (att.recording_url || "").match(/\/recording\/(\d+)/)?.[1];
                            return (
                              <tr key={att.id || att.external_id || `att-${i}-${ai}`} data-testid={`rep-detail-attempt-${i}-${ai}`} style={{ borderTop: "0.5px solid #fef3c7" }}>
                                <td style={{ padding: "5px 8px", color: "#78350f", fontWeight: 600, whiteSpace: "nowrap" }}>
                                  #{ai + 1}
                                  {att.is_anchor && (
                                    <span title="Anchor row of the merged cluster" style={{ marginLeft: 4, fontSize: 8, color: "#92400e", fontWeight: 700 }}>◆</span>
                                  )}
                                </td>
                                <td style={{ padding: "5px 8px", color: "#475569", whiteSpace: "nowrap" }}>
                                  {att.at ? new Date(att.at).toLocaleTimeString() : "—"}
                                </td>
                                <td style={{ padding: "5px 8px", color: "#475569", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }} data-testid={`rep-detail-attempt-phone-${i}-${ai}`}>
                                  {att.phone || "—"}
                                </td>
                                <td style={{ padding: "5px 8px" }}>
                                  <span style={{ display: "inline-block", padding: "1px 6px", background: attOc.bg, color: attOc.color, borderRadius: 4, fontSize: 9, fontWeight: 600, textTransform: "uppercase" }}>
                                    {att.outcome || "—"}
                                  </span>
                                </td>
                                <td style={{ padding: "5px 8px", color: "#475569", fontVariantNumeric: "tabular-nums" }}>
                                  {fmtSec(att.duration_sec || 0)}
                                </td>
                                <td style={{ padding: "5px 8px" }}>
                                  <CopyableId value={att.external_id} label="RC call ID" testid={`rep-detail-attempt-callid-${i}-${ai}`} />
                                </td>
                                <td style={{ padding: "5px 8px" }}>
                                  {recId ? (
                                    <RCRecordingPlayer
                                      recordingId={recId}
                                      agentId={agentId}
                                      token={token}
                                      testid={`rep-detail-attempt-audio-${i}-${ai}`}
                                    />
                                  ) : (
                                    <span style={{ color: "#cbd5e1", fontSize: 9 }}>—</span>
                                  )}
                                </td>
                                <td style={{ padding: "5px 8px", color: "#475569", textTransform: "capitalize" }}>
                                  {att.sentiment || "—"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {/* Iter 342 — pagination footer. Only renders when the total
          exceeds one page. Mirrors the Iter 337 lead activity tab
          pattern. Also shows a truthful "Showing X-Y of TOTAL" line
          so admins can see at a glance how much data lives beyond
          the current page. */}
      {(detail.total_pages || 1) > 1 && (
        <RepDetailPager
          agentId={agentId}
          page={page}
          pageSize={pageSize}
          totalPages={detail.total_pages || 1}
          total={detail.total || rows.length}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}


function RepDetailPager({ agentId, page, pageSize, totalPages, total, onPageChange }) {
  const startRow = (page - 1) * pageSize + 1;
  const endRow = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;
  const btnBase = {
    padding: "5px 10px", fontSize: 11, fontWeight: 600,
    border: "0.5px solid #cbd5e1", background: "#fff", color: "#1e293b",
    borderRadius: 6, cursor: "pointer", fontFamily: "inherit",
  };
  const btnDisabled = { ...btnBase, opacity: 0.4, cursor: "not-allowed", background: "#f8fafc" };
  return (
    <div
      data-testid={`rep-detail-pager-${agentId}`}
      style={{
        marginTop: 10, padding: "8px 12px",
        background: "#fff", border: "0.5px solid #e2e8f0", borderRadius: 8,
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: 10, flexWrap: "wrap", fontSize: 11, color: "#64748b",
      }}
    >
      <span data-testid={`rep-detail-pager-status-${agentId}`}>
        Showing <strong style={{ color: "#0f172a" }}>{startRow.toLocaleString()}–{endRow.toLocaleString()}</strong>{" "}
        of <strong style={{ color: "#0f172a" }}>{total.toLocaleString()}</strong>
        <span style={{ opacity: 0.6 }}> · Page {page} of {totalPages}</span>
      </span>
      <div style={{ display: "flex", gap: 6 }}>
        <button data-testid={`rep-detail-first-${agentId}`} onClick={() => onPageChange(1)} disabled={!canPrev}
                style={canPrev ? btnBase : btnDisabled}>« First</button>
        <button data-testid={`rep-detail-prev-${agentId}`} onClick={() => onPageChange(page - 1)} disabled={!canPrev}
                style={canPrev ? btnBase : btnDisabled}>◀ Prev</button>
        <button data-testid={`rep-detail-next-${agentId}`} onClick={() => onPageChange(page + 1)} disabled={!canNext}
                style={canNext ? btnBase : btnDisabled}>Next ▶</button>
        <button data-testid={`rep-detail-last-${agentId}`} onClick={() => onPageChange(totalPages)} disabled={!canNext}
                style={canNext ? btnBase : btnDisabled}>Last »</button>
      </div>
    </div>
  );
}


/* ── Iter 174.4 (Danny) — Call Review modal redesign ──────────────────
 * Centered 940px modal, two-panel body. Replaces the right-edge
 * slide-out and the multi-channel timeline (those still live in Lead
 * Focus). Strict mock parity per Danny's spec:
 *   header        — eyebrow + title + badges + meta row + close
 *   LEFT panel    — Performance scores (Agent/Lead tabs), Call facts
 *                   grid, Lead context strip, single Follow-up task
 *   RIGHT panel   — AI summary card (headline + What/Risks/Next),
 *                   Coaching feedback card, collapsible Transcript
 * Coaching wires to POST /api/admin/rep-activity/coaching-feedback.
 * AI draft wires to POST /api/admin/rep-activity/coaching-feedback/ai-draft.
 */

// Iter 174.4 fix: robust error→string helper. FastAPI/Pydantic v2 returns
// `detail` as an ARRAY of {type,loc,msg,...} on 422 validation errors.
// String-concatenating that produces '[object Object]', which we'd show
// in toasts. This helper extracts the most useful human string.
function _readErr(e) {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) {
    const parts = d.map((x) => x?.msg || x?.message || JSON.stringify(x)).filter(Boolean);
    return parts.join("; ") || `HTTP ${e?.response?.status}`;
  }
  if (typeof d === "string") return d;
  if (d && typeof d === "object") return d.msg || d.message || JSON.stringify(d);
  return e?.message || "Unknown error";
}

function CallDetailLightbox({ isEhi = false, state, aiSummary, onClose, onRunAI, onOpenTranscript, apiBase, authHeaders }) {
  const { loading, data, error, external_id } = state;
  const lead = data?.lead || {};
  const call = data?.call || {};
  const disp = data?.disposition || null;
  const followupTasks = data?.followup_tasks || [];
  const navigate = useNavigate();

  // Pull rich transcript meta for the AI Summary card body.
  const [richTx, setRichTx] = useState({ loading: false, data: null, error: null });
  const loadRichTx = React.useCallback(async () => {
    if (!external_id) return;
    setRichTx({ loading: true, data: null, error: null });
    try {
      const r = await axios.get(`${apiBase}/dialer/call-transcript/${encodeURIComponent(external_id)}`, { headers: authHeaders() });
      setRichTx({ loading: false, data: r.data, error: null });
    } catch (e) {
      const status = e?.response?.status;
      // Iter 177.5 (Danny #ffa8855a) — backend now returns a structured
      // `detail` object for 202 + 503 (transcription_abandoned). Pull
      // the human-readable message out of the object so the rep sees
      // "Transcription failed after 3 attempts… ask admin to retry"
      // instead of "[object Object]".
      const rawDetail = e?.response?.data?.detail;
      const detailMsg = (rawDetail && typeof rawDetail === "object")
        ? (rawDetail.message || rawDetail.code || "Couldn't load transcript")
        : (rawDetail || e.message || "Couldn't load transcript");
      setRichTx({ loading: false, data: null, error: status === 404 ? null : detailMsg });
    }
  }, [external_id, apiBase, authHeaders]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (external_id) loadRichTx(); }, [external_id]);

  // Derived display strings.
  const fmtDur = (sec) => {
    if (sec == null) return "—";
    const s = Math.max(0, Math.floor(Number(sec) || 0));
    const m = Math.floor(s / 60); const r = s % 60;
    return m > 0 ? `${m}m ${r}s` : `${r}s`;
  };
  const callDate = call.at ? new Date(call.at) : null;
  const dateLabel = callDate ? callDate.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";
  const timeLabel = callDate ? callDate.toLocaleString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }) : "";
  const durLabel = fmtDur(call.duration_sec || call.duration);
  const repName = call.user_name || data?.agent?.name || call.from_name || "—";
  const callType = (call.call_type || disp?.call_type || (call.direction === "outbound" ? "Outbound" : "Inbound")).toString();
  const aiProcessed = !!(richTx.data?.ai_takeaway || richTx.data?.summary || richTx.data?.key_points?.length || aiSummary?.text);
  const titleText = lead.company || lead.practice || lead.name || "Untitled call";

  // Outcome → "Live conversation" / "Voicemail" / "No answer" / "Demo booked" badge.
  const outcomeKey = (call.outcome || "").toLowerCase();
  const outcomeBadgeMap = {
    "connected":   { bg: "#dcfce7", color: "#15803d", label: "Live conversation" },
    "demo-booked": { bg: "#dcfce7", color: "#15803d", label: "Demo booked" },
    "voicemail":   { bg: "#dbeafe", color: "#1e40af", label: "Voicemail" },
    "no-answer":   { bg: "#f3f4f6", color: "#4b5563", label: "No answer" },
    "busy":        { bg: "#fef3c7", color: "#854d0e", label: "Busy" },
  };
  const outcomeBadge = outcomeBadgeMap[outcomeKey] || { bg: "#f3f4f6", color: "#4b5563", label: outcomeKey || "—" };

  // Contact role (from sub-status or disposition payload).
  const contactRoleLabel = (() => {
    const s = lead.sub_status || disp?.substatus;
    if (s) return String(s).replace(/^Contacted —\s*/i, "");
    if (disp?.contact_role) return disp.contact_role.replace(/_/g, " ");
    return null;
  })();

  // Stage / call-number badge.
  const callNoLabel = (() => {
    const ac = data?.attempt_counter;
    if (ac) return `Call #${ac.call_number} of ${ac.total_in_stage} · ${ac.stage_label || "Contacted"}`;
    if (lead.stage) return String(lead.stage).replace(/_/g, " ");
    return null;
  })();

  // ── Performance scores ──
  const [scoreTab, setScoreTab] = useState("agent");
  const activityId = call.id || state?.activity_id;
  const leadAiScore = lead?.ai_score ?? richTx.data?.lead_score;

  // ── Coaching ──
  const [coachingText, setCoachingText] = useState("");
  const [coachingDraftBusy, setCoachingDraftBusy] = useState(false);
  const [coachingSendBusy, setCoachingSendBusy] = useState(false);
  const [coachingSent, setCoachingSent] = useState(false);
  const aiDraftCoaching = async () => {
    setCoachingDraftBusy(true);
    try {
      const r = await axios.post(
        `${apiBase}/admin/rep-activity/coaching-feedback/ai-draft`,
        { activity_id: activityId, external_id },
        { headers: authHeaders() },
      );
      setCoachingText(r.data?.draft || "");
    } catch (e) {
      toast.error("AI draft failed: " + _readErr(e));
    } finally {
      setCoachingDraftBusy(false);
    }
  };
  const sendCoaching = async () => {
    if (!coachingText.trim()) { toast.error("Add text first."); return; }
    setCoachingSendBusy(true);
    try {
      const fd = new FormData();
      // Iter 174.4 fix: parent passes agent_id on state.agent_id — that's
      // the source of truth. Falling back to call.user_id is risky
      // because the per-rep calls payload doesn't always include it,
      // causing FastAPI Form(...) to 422 on empty-string.
      const agentId = state?.agent_id || call.user_id || data?.agent?.id || "";
      fd.append("agent_id", agentId);
      if (activityId) fd.append("activity_id", activityId);
      if (external_id) fd.append("external_id", external_id);
      if (lead.id) fd.append("lead_id", lead.id);
      fd.append("text", coachingText.trim());
      await axios.post(`${apiBase}/admin/rep-activity/coaching-feedback`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      toast.success("Coaching sent — the rep will see it in their inbox.");
      setCoachingText("");
      setCoachingSent(true);
      setTimeout(() => setCoachingSent(false), 3500);
    } catch (e) {
      toast.error("Send failed: " + _readErr(e));
    } finally {
      setCoachingSendBusy(false);
    }
  };

  // ── Transcript collapsible ──
  // Iter 177 (Danny #73cdf3ac Feb 2026) — transcripts default expanded.
  // Reps & admins click into a call specifically to read the transcript;
  // forcing them to first click a 'Call transcript' toggle to reveal
  // what's clearly the headline content was the actual UX of "transcripts
  // are not loading". The whole point of the lightbox is the transcript.
  // Iter 344 — right-column accordion. Exactly ONE of the three
  // right-column sections (AI summary / Coaching feedback / Call
  // transcript) is open at a time; AI summary is open by default and
  // resets on every open (this component unmounts on close). Clicking the
  // already-open section is a no-op, so all-collapsed is never reachable.
  const [openSection, setOpenSection] = useState("summary");
  const secOpen = (k) => openSection === k;
  // Iter 191 (Peter dc10f22f) — full-screen pop-out for long transcripts.
  const [transcriptFullOpen, setTranscriptFullOpen] = useState(false);
  // ESC closes the full-screen transcript modal (Peter's long-transcript flow).
  useEffect(() => {
    if (!transcriptFullOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setTranscriptFullOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [transcriptFullOpen]);
  // Iter 174.4 fix: the rich-transcript payload sometimes returns
  // `transcript` as a string (full text) or an object (e.g. {full_text}),
  // not always an array. Guard with Array.isArray so the .map() below
  // doesn't crash and trigger RouteErrorBoundary.
  const _rawTxLines = richTx.data?.transcript ?? richTx.data?.lines ?? richTx.data?.dialogue ?? [];
  const transcriptLines = Array.isArray(_rawTxLines) ? _rawTxLines : [];
  const transcriptFullText = (!Array.isArray(_rawTxLines) && typeof _rawTxLines === "string")
    ? _rawTxLines
    : (_rawTxLines && typeof _rawTxLines === "object" && typeof _rawTxLines.full_text === "string")
      ? _rawTxLines.full_text
      : "";

  // Iter 344.1 — quick transcript indicators shown in the (always-visible)
  // transcript title bar: total words + the AI Human Connect verdict.
  const _txText = (transcriptFullText || transcriptLines.map((l) => (l?.text || l?.utterance || "")).join(" ")).trim();
  const txWordCount = _txText ? _txText.split(/\s+/).filter(Boolean).length : 0;
  const hcVerdict = (state.human_connect_verdict !== undefined && state.human_connect_verdict !== null)
    ? state.human_connect_verdict
    : (call.human_connect_verdict ?? richTx.data?.human_connect_verdict ?? null);

  // ── AI summary derived bullets ──
  const headline = (richTx.data?.ai_takeaway || richTx.data?.summary || aiSummary?.text || "").toString().trim();
  const whatHappened = (richTx.data?.key_points || []).map((k) => typeof k === "string" ? k : (k?.text || JSON.stringify(k)));
  const nextActions = (richTx.data?.action_items || []).map((a) => typeof a === "string" ? a : (a?.text || a?.title || JSON.stringify(a)));
  const sentiment = richTx.data?.sentiment_final || disp?.sentiment || "";
  // Iter 191 (Peter dc10f22f) — Show an explicit "Spoke to" line so the
  // reviewer can tell at a glance whether the rep reached a human and
  // (when known) what role they were. Derived from is_live_human + the
  // lead's sub_status / disposition payload. Always rendered above the
  // headline so it's never missed.
  const spokeToInfo = (() => {
    const isLive = call.is_live_human;
    const oc = (call.outcome || "").toLowerCase();
    const sub = (lead.sub_status || disp?.substatus || "").toString();
    const cleaned = sub.replace(/^Contacted —\s*/i, "").trim();
    if (oc === "voicemail" || isLive === false) {
      return { label: "Voicemail / no human contact", color: "#92400e", bg: "#fef3c7", icon: "📭" };
    }
    if (oc === "no-answer" || oc === "no_answer") {
      return { label: "No answer — no contact made", color: "#4b5563", bg: "#f3f4f6", icon: "📵" };
    }
    if (oc === "busy") {
      return { label: "Line busy — no contact made", color: "#4b5563", bg: "#f3f4f6", icon: "📵" };
    }
    if (oc === "ivr" || /ivr|automated/i.test(sub)) {
      return { label: "Stuck in IVR / automated menu", color: "#92400e", bg: "#fef3c7", icon: "🤖" };
    }
    if (isLive === true || oc === "connected" || oc === "demo-booked") {
      if (cleaned) {
        return { label: `Spoke to: ${cleaned}`, color: "#15803d", bg: "#dcfce7", icon: "🗣️" };
      }
      return { label: "Spoke to a human (role not tagged yet)", color: "#15803d", bg: "#dcfce7", icon: "🗣️" };
    }
    return { label: "Contact level not yet classified", color: "#6b7280", bg: "#f3f4f6", icon: "❔" };
  })();
  const risks = [];
  if ((sentiment || "").toLowerCase() === "negative") risks.push(`${isEhi ? "Client" : "Lead"} sentiment trended negative — adjust framing on next touch.`);
  if (disp && disp.decision_maker_reached === false) risks.push("Decision-maker not reached on this attempt.");
  if (disp && disp.budget_confirmed === false) risks.push("Budget unconfirmed — surface ROI talking points next time.");
  if (outcomeKey === "voicemail") risks.push("Left voicemail — schedule follow-up within 48 hours.");

  return (
    <div
      data-testid="call-detail-lightbox"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(15,23,42,0.55)", backdropFilter: "blur(4px)",
        zIndex: 9999, display: "flex", alignItems: "flex-start", justifyContent: "center",
        padding: "28px 20px", overflowY: "auto",
      }}
    >
      <div
        data-testid="call-review-modal"
        style={{
          background: "#fff", border: "1px solid #e5e7eb", borderRadius: 14,
          overflow: "hidden",
          // Iter 175.5 (Danny #15 deeper): widened from 940px to 1280px
          // and made width fluid (min(1280, 92vw)) so on wide screens
          // the transcript and the agent-score column both get full
          // breathing room. Previously a static 940px clipped the
          // agent-score block at the right margin on ≥1440px monitors.
          width: "min(1280px, 92vw)",
          maxWidth: "1280px",
          // Height stays bounded but a bit taller so transcript can
          // breathe — body scrolls as a unit.
          minHeight: "85vh", maxHeight: "92vh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 8px 32px rgba(0,0,0,0.09), 0 2px 8px rgba(0,0,0,0.06)",
          fontFamily: "'Inter', system-ui, sans-serif", color: "#111827",
        }}
      >
        {/* ───── HEADER ───── */}
        <div style={{ padding: "14px 20px 12px", background: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
          <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", color: "#9ca3af", marginBottom: 5 }}>
            Call review
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
            <div data-testid="cr-title" style={{ fontSize: 18, fontWeight: 600, color: "#111827", letterSpacing: "-0.3px" }}>
              {titleText}
            </div>
            <CRBadge testid="cr-outcome-badge" bg={outcomeBadge.bg} color={outcomeBadge.color}>{outcomeBadge.label}</CRBadge>
            {contactRoleLabel && <CRBadge testid="cr-contact-role-badge" bg="#f3f4f6" color="#4b5563">{contactRoleLabel}</CRBadge>}
            {callNoLabel && <CRBadge testid="cr-call-no-badge" bg="#dbeafe" color="#1d4ed8">{callNoLabel}</CRBadge>}
            <button
              data-testid="cr-close"
              onClick={onClose}
              aria-label="Close"
              style={{
                marginLeft: "auto", width: 28, height: 28, borderRadius: "50%",
                border: "1px solid #e5e7eb", background: "#fff", cursor: "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                color: "#6b7280", fontSize: 16, transition: "all .12s",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.color = "#111827"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#6b7280"; }}
            >
              ×
            </button>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: "#6b7280", marginTop: 6, flexWrap: "wrap" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <FileText style={{ width: 12, height: 12 }} />{dateLabel}{timeLabel ? ` · ${timeLabel}` : ""}
            </span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Clock style={{ width: 12, height: 12 }} />{durLabel}
            </span>
            <span style={{ color: "#d1d5db" }}>·</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Users style={{ width: 12, height: 12 }} />{repName} <span style={{ color: "#9ca3af" }}>({callType})</span>
            </span>
            {aiProcessed && (
              <>
                <span style={{ color: "#d1d5db" }}>·</span>
                <span data-testid="cr-ai-processed" style={{ display: "inline-flex", alignItems: "center", gap: 4, color: "#15803d", fontWeight: 600 }}>
                  <Check style={{ width: 12, height: 12 }} /> AI processed
                </span>
              </>
            )}
          </div>
          {/* Iter 220 (Aug 2026 · UX consolidation) — Call ID +
              Recording ID + wall-clock timing detail were moved off
              the Rep Activity drill-down table (where they cluttered
              the row) and consolidated here so admins can still copy
              them one click away. Wall-detail block (start→end times,
              wall duration, ISO+TZ tooltip) mirrors the same
              renderWhenTimes(...) helper that used to live inline.
              Iter 220.1 (Aug 2026) — mirror the same recording-ID
              fallback used by the drill-down row (`row.recording_id
              || row.recording_url.match(/\/recording\/(\d+)/)?.[1]`)
              so calls whose call-detail payload only carries the
              media URL (not the parsed ID) still surface the ID
              here. Previously the strip silently hid the Recording
              ID for those rows even though it existed in the URL. */}
          {(() => {
            const _recId = call.recording_id
              || (typeof call.recording_url === "string" ? (call.recording_url.match(/\/recording\/(\d+)/) || [])[1] : null);
            const _showStrip = call.external_id || _recId || call.started_at || call.at;
            if (!_showStrip) return null;
            return (
              <div
                data-testid="cr-diagnostics-strip"
                style={{
                  display: "flex", alignItems: "center", gap: 10,
                  flexWrap: "wrap", marginTop: 8, paddingTop: 8,
                  borderTop: "1px dashed #e5e7eb",
                  fontSize: 11, color: "#6b7280",
                }}
              >
                {call.external_id && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#9ca3af" }}>Call ID</span>
                    <CopyableId value={call.external_id} label="RC call ID" testid="cr-callid-copy" />
                  </span>
                )}
                {call.external_id && _recId && <span style={{ color: "#d1d5db" }}>·</span>}
                {_recId && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#9ca3af" }}>Recording ID</span>
                    <CopyableId value={_recId} label="Recording ID" testid="cr-recid-copy" />
                  </span>
                )}
                {(call.external_id || _recId) && (call.started_at || call.at) && <span style={{ color: "#d1d5db" }}>·</span>}
                {(call.started_at || call.at) && renderWhenTimes(call, "cr-when-times")}
              </div>
            );
          })()}
        </div>

        {/* ───── BODY: 2-column ───── */}
        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280", fontSize: 13 }}>
            <Loader2 style={{ width: 16, height: 16, animation: "spin 0.8s linear infinite", verticalAlign: "middle", marginRight: 6 }} />
            Loading call details…
          </div>
        ) : error ? (
          <div style={{ padding: 32, fontSize: 12, color: "#991b1b", background: "#fef2f2" }}>{error}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", flex: 1, minHeight: 0 }}>
            {/* ── LEFT PANEL ── */}
            <aside
              data-testid="cr-left-panel"
              style={{
                background: "#f9fafb", borderRight: "1px solid #e5e7eb",
                padding: 14, display: "flex", flexDirection: "column", gap: 14,
                overflowY: "auto", minHeight: 0,
              }}
            >
              {/* Performance scores */}
              <section data-testid="cr-perf-scores">
                <SecLabel>Performance scores</SecLabel>
                <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #e5e7eb", marginBottom: 10 }}>
                  <ScoreTab active={scoreTab === "agent"} onClick={() => setScoreTab("agent")} testid="cr-score-tab-agent">Agent score</ScoreTab>
                  <ScoreTab active={scoreTab === "lead"}  onClick={() => setScoreTab("lead")}  testid="cr-score-tab-lead">{isEhi ? "Client score" : "Lead score"}</ScoreTab>
                </div>
                {scoreTab === "agent" ? (
                  activityId
                    ? <CallScorePanel activityId={activityId} isManager={true} />
                    : <div style={{ fontSize: 11, color: "#9ca3af" }}>No activity id — score unavailable.</div>
                ) : (
                  <LeadScoreCard score={leadAiScore} sentiment={sentiment} />
                )}
              </section>

              {/* Call facts */}
              <section data-testid="cr-call-facts">
                <SecLabel>Call facts</SecLabel>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                  <Tile label="Outcome"  value={outcomeBadge.label} tone={outcomeKey === "connected" || outcomeKey === "demo-booked" ? "green" : (outcomeKey === "voicemail" ? "blue" : "neutral")} />
                  <Tile label="Duration" value={durLabel} />
                  <Tile label="Decision-maker" value={disp?.decision_maker_reached === true ? "Reached" : disp?.decision_maker_reached === false ? "Not reached" : "—"} tone={disp?.decision_maker_reached ? "green" : disp?.decision_maker_reached === false ? "red" : "neutral"} />
                  <Tile label="Budget" value={disp?.budget_confirmed === true ? "Confirmed" : disp?.budget_confirmed === false ? "Unconfirmed" : "—"} tone={disp?.budget_confirmed ? "green" : disp?.budget_confirmed === false ? "amber" : "neutral"} />
                  <Tile label="Next step" value={disp?.next_step || "—"} full />
                  <Tile label="Follow-up" value={disp?.followup_date ? new Date(disp.followup_date).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true }) : "—"} full />
                  <Tile label="Sentiment" value={sentiment ? sentiment.charAt(0).toUpperCase() + sentiment.slice(1) : "—"} tone={sentiment === "positive" ? "green" : sentiment === "negative" ? "red" : "neutral"} />
                  <Tile label="Stage movement" value={disp?.stage_from && disp?.stage_to ? `${disp.stage_from} → ${disp.stage_to}` : (lead.stage || "—")} />
                </div>
              </section>

              {/* Iter 343 — Products Discussed (Iter 340). Only renders
                  when the rep actually tagged products or when the AI
                  auto-detected them from the transcript. */}
              {(disp?.product_tags && disp.product_tags.length > 0) && (
                <section data-testid="cr-products-discussed" style={{ marginBottom: 14 }}>
                  <SecLabel>Products Discussed</SecLabel>
                  <div style={{ padding: "6px 0" }}>
                    <ProductTagPills tags={disp.product_tags} source={disp?.ai_applied?.product_tags_source} size="lg" />
                  </div>
                </section>
              )}

              {/* Iter 343 — Timeline note the rep typed on save. */}
              {disp?.timeline_note && (
                <section data-testid="cr-timeline-note" style={{ marginBottom: 14 }}>
                  <SecLabel>Timeline note</SecLabel>
                  <div style={{
                    padding: "10px 12px", background: "#f8fafc",
                    border: "0.5px solid #e2e8f0", borderRadius: 8,
                    fontSize: 12, color: "#0f172a", whiteSpace: "pre-wrap",
                  }}>{disp.timeline_note}</div>
                </section>
              )}

              {/* Iter 343 — Attachments the rep pinned to this call. */}
              {Array.isArray(disp?.attachments) && disp.attachments.length > 0 && (
                <section data-testid="cr-attachments" style={{ marginBottom: 14 }}>
                  <SecLabel>Attachments ({disp.attachments.length})</SecLabel>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {disp.attachments.map((a, i) => (
                      <span key={a?.id || i} data-testid={`cr-attachment-${i}`} style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "4px 10px", background: "#eff6ff",
                        border: "0.5px solid #bfdbfe", borderRadius: 999,
                        fontSize: 11, color: "#1e40af", fontWeight: 500,
                      }}>
                        {a?.name || a?.title || a?.id || `Attachment ${i + 1}`}
                        {a?.kind && <span style={{ opacity: 0.6, fontSize: 10 }}>· {a.kind}</span>}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Lead context strip */}
              <section data-testid="cr-lead-context">
                <SecLabel>{isEhi ? "Client context" : "Lead context"}</SecLabel>
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, fontSize: 11 }}>
                    <Ctx k="Stage" v={lead.stage ? String(lead.stage).replace(/_/g, " ") : "—"} />
                    <Ctx k="Sub-status" v={lead.sub_status ? String(lead.sub_status).replace(/^Contacted —\s*/i, "") : "—"} />
                    <Ctx k="Company" v={lead.company || lead.practice || "—"} />
                    {data?.attempt_counter && (
                      <Ctx k="Call #" v={`${data.attempt_counter.call_number} of ${data.attempt_counter.total_in_stage}`} />
                    )}
                  </div>
                  {lead.id && (
                    <button
                      data-testid="cr-open-lead"
                      onClick={() => {
                        try {
                          // Iter 192q — preserve source URL for back-nav
                          sessionStorage.setItem("leadFocusReturn", JSON.stringify({
                            url: window.location.pathname + window.location.search,
                            label: "Rep Activity Report",
                            ts: Date.now(),
                          }));
                        } catch { /* non-fatal */ }
                        navigate(String(lead.id).startsWith("ehi-") ? `/ehi-clients/${String(lead.id).slice(4)}` : `/lead-focus/${lead.id}`);
                        onClose();
                      }}
                      style={{
                        marginTop: 8, fontSize: 11, color: "#2563eb",
                        background: "transparent", border: "none", padding: 0, cursor: "pointer",
                        display: "inline-flex", alignItems: "center", gap: 3, fontFamily: "inherit",
                      }}
                    >
                      {isEhi || String(lead.id).startsWith("ehi-") ? "Open client" : "Open full lead profile"} <ExternalLink style={{ width: 10, height: 10 }} />
                    </button>
                  )}
                </div>
              </section>

              {/* Follow-up task */}
              {followupTasks.length > 0 && (
                <section data-testid="cr-followup-task">
                  <SecLabel>Follow-up task</SecLabel>
                  {followupTasks.slice(0, 1).map((t) => (
                    <div key={t.id} style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <div
                        style={{
                          width: 15, height: 15, border: `1.5px solid ${t.status === "done" ? "#16a34a" : "#d1d5db"}`,
                          background: t.status === "done" ? "#16a34a" : "transparent",
                          borderRadius: 3, marginTop: 2, flexShrink: 0,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}
                      >
                        {t.status === "done" && <Check style={{ width: 10, height: 10, color: "#fff" }} />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11.5, fontWeight: 500, color: "#111827" }}>{t.title}</div>
                        {(t.due_at || t.priority) && (
                          <div style={{ marginTop: 4, display: "flex", gap: 5, flexWrap: "wrap" }}>
                            {t.due_at && <Tag>{new Date(t.due_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</Tag>}
                            {t.priority && <Tag>{t.priority}</Tag>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </section>
              )}
            </aside>

            {/* ── RIGHT PANEL ── */}
            <div
              data-testid="cr-right-panel"
              style={{
                padding: 16, display: "flex", flexDirection: "column", gap: 12,
                overflow: "hidden", minHeight: 0,
              }}
            >
              {/* AI summary card */}
              <section data-testid="cr-ai-summary" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, display: "flex", flexDirection: "column", overflow: "hidden", ...(secOpen("summary") ? { flex: "1 1 auto", minHeight: 0 } : { flex: "0 0 auto" }) }}>
                <div
                  data-testid="cr-ai-summary-header"
                  onClick={() => setOpenSection("summary")}
                  style={{ padding: "11px 14px", borderBottom: secOpen("summary") ? "1px solid #e5e7eb" : "none", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: "0 0 auto", transition: "background .12s" }}
                  onMouseEnter={(e) => { if (!secOpen("summary")) e.currentTarget.style.background = "#f9fafb"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
                >
                  <Sparkles style={{ width: 13, height: 13, color: "#2563eb" }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>AI summary</div>
                    <div style={{ fontSize: 10.5, color: "#9ca3af" }}>Generated from call transcript</div>
                  </div>
                  <button
                    data-testid="cr-regenerate-summary"
                    onClick={(e) => { e.stopPropagation(); onRunAI && onRunAI(); }}
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4,
                      fontSize: 11.5, padding: "5px 11px", border: "1px solid #e5e7eb",
                      borderRadius: 20, background: "#fff", color: "#6b7280",
                      cursor: "pointer", transition: "all .12s", fontFamily: "inherit",
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = "#f3f4f6"; e.currentTarget.style.color = "#374151"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#6b7280"; }}
                  >
                    <RefreshCw style={{ width: 11, height: 11 }} /> Regenerate
                  </button>
                  <ChevronDown data-testid="cr-ai-summary-chevron" style={{ width: 14, height: 14, color: "#9ca3af", transform: secOpen("summary") ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .3s cubic-bezier(0.22,0.61,0.36,1)", flexShrink: 0 }} />
                </div>
                {secOpen("summary") && (
                <div data-testid="cr-ai-summary-body" style={{ padding: 14, flex: "1 1 auto", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", animation: "crSectionExpand 0.42s cubic-bezier(0.22,0.61,0.36,1)" }}>
                  {/* Iter 191 (Peter dc10f22f) — "Spoke to" indicator. Always
                      shown so the reviewer can tell at a glance whether
                      the rep reached a human, voicemail, or IVR. */}
                  <div
                    data-testid="cr-spoke-to"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "5px 10px", borderRadius: 8,
                      background: spokeToInfo.bg, color: spokeToInfo.color,
                      fontSize: 12, fontWeight: 600, marginBottom: 12,
                    }}
                  >
                    <span aria-hidden>{spokeToInfo.icon}</span>
                    {spokeToInfo.label}
                  </div>
                  {headline ? (
                    <div data-testid="cr-summary-headline" style={{ fontSize: 13.5, fontWeight: 500, color: "#111827", lineHeight: 1.5, marginBottom: 12 }}>
                      {headline}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 12, fontStyle: "italic" }}>
                      No AI summary yet — click Regenerate to produce one.
                    </div>
                  )}
                  {(whatHappened.length > 0 || risks.length > 0 || nextActions.length > 0) && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                      {whatHappened.length > 0 && (
                        <SummarySection testid="cr-summary-what" color="#2563eb" label="What happened" items={whatHappened} />
                      )}
                      {risks.length > 0 && (
                        <SummarySection testid="cr-summary-risks" color="#dc2626" label="Risks" items={risks} />
                      )}
                      {nextActions.length > 0 && (
                        <SummarySection testid="cr-summary-next" color="#15803d" label="Recommended next actions" items={nextActions} />
                      )}
                    </div>
                  )}
                </div>
                )}
              </section>

              {/* Coaching feedback card */}
              <section data-testid="cr-coaching" style={{ background: "#fff", border: "1px solid #fde68a", borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column", ...(secOpen("coaching") ? { flex: "1 1 auto", minHeight: 0 } : { flex: "0 0 auto" }) }}>
                <div
                  data-testid="cr-coaching-header"
                  onClick={() => setOpenSection("coaching")}
                  style={{ padding: "10px 14px", background: "#fffbeb", borderBottom: secOpen("coaching") ? "1px solid #fde68a" : "none", display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flex: "0 0 auto", transition: "filter .12s" }}
                  onMouseEnter={(e) => { if (!secOpen("coaching")) e.currentTarget.style.filter = "brightness(0.97)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
                >
                  <MessageCircle style={{ width: 13, height: 13, color: "#92400e" }} />
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#92400e", flex: 1 }}>Coaching feedback</div>
                  <span style={{ fontSize: 10, color: "#a16207" }}>Visible to the rep only</span>
                  <ChevronDown data-testid="cr-coaching-chevron" style={{ width: 14, height: 14, color: "#a16207", transform: secOpen("coaching") ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .3s cubic-bezier(0.22,0.61,0.36,1)", flexShrink: 0 }} />
                </div>
                {secOpen("coaching") && (
                <div data-testid="cr-coaching-body" style={{ padding: 14, flex: "1 1 auto", minHeight: 0, overflowY: "auto", overscrollBehavior: "contain", animation: "crSectionExpand 0.42s cubic-bezier(0.22,0.61,0.36,1)" }}>
                  <textarea
                    data-testid="cr-coaching-textarea"
                    value={coachingText}
                    onChange={(e) => setCoachingText(e.target.value)}
                    placeholder="Write coaching feedback for this call…"
                    rows={4}
                    style={{
                      width: "100%", padding: 10, fontSize: 12.5, lineHeight: 1.5,
                      border: "1px solid #fde68a", borderRadius: 7,
                      background: "#fffef9", color: "#111827", resize: "vertical",
                      outline: "none", fontFamily: "inherit", boxSizing: "border-box",
                    }}
                  />
                  <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <button
                      data-testid="cr-coaching-record"
                      title="Voice recording is available via the full coaching panel below."
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 5,
                        fontSize: 12, color: "#92400e", background: "transparent",
                        border: "1px solid #fde68a", borderRadius: 7, padding: "6px 11px",
                        cursor: "pointer", fontFamily: "inherit",
                      }}
                    >
                      <Mic style={{ width: 12, height: 12 }} /> Record voice
                    </button>
                    <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
                      <button
                        data-testid="cr-coaching-ai-draft"
                        onClick={aiDraftCoaching}
                        disabled={coachingDraftBusy}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          fontSize: 12, color: "#6d28d9", background: "#f5f3ff",
                          border: "1px solid #c4b5fd", borderRadius: 7, padding: "6px 11px",
                          cursor: coachingDraftBusy ? "wait" : "pointer", fontFamily: "inherit",
                          opacity: coachingDraftBusy ? 0.7 : 1,
                        }}
                      >
                        {coachingDraftBusy ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Sparkles style={{ width: 12, height: 12 }} />}
                        AI draft
                      </button>
                      <button
                        data-testid="cr-coaching-send"
                        onClick={sendCoaching}
                        disabled={coachingSendBusy}
                        style={{
                          display: "inline-flex", alignItems: "center", gap: 5,
                          background: "#1f2937", color: "#fff", border: "none",
                          borderRadius: 7, padding: "6px 15px", fontSize: 12.5,
                          fontWeight: 500, cursor: coachingSendBusy ? "wait" : "pointer",
                          fontFamily: "inherit", opacity: coachingSendBusy ? 0.7 : 1,
                        }}
                      >
                        {coachingSendBusy ? <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite" }} /> : <Send style={{ width: 12, height: 12 }} />}
                        Send
                      </button>
                    </div>
                  </div>
                  {coachingSent && (
                    <div data-testid="cr-coaching-sent" style={{ marginTop: 10, fontSize: 11.5, color: "#15803d", display: "inline-flex", alignItems: "center", gap: 4 }}>
                      <Check style={{ width: 12, height: 12 }} /> Coaching sent — the rep will see it in their inbox.
                    </div>
                  )}
                  {/* Full coaching panel (with voice recording + history) lives below in a small disclosure */}
                  <details data-testid="cr-coaching-details" style={{ marginTop: 12 }}>
                    <summary style={{ fontSize: 11, color: "#92400e", cursor: "pointer", listStyle: "none" }}>
                      ▸ Open full coaching panel (voice + history)
                    </summary>
                    <div style={{ marginTop: 10 }}>
                      <CoachingFeedbackPanel
                        apiBase={apiBase}
                        authHeaders={authHeaders}
                        agentId={call.user_id || data?.agent?.id || ""}
                        activityId={activityId}
                        externalId={external_id}
                        leadId={lead.id}
                      />
                    </div>
                  </details>
                </div>
                )}
              </section>

              {/* Transcript */}
              <section data-testid="cr-transcript" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column", ...(secOpen("transcript") ? { flex: "1 1 auto", minHeight: 0 } : { flex: "0 0 auto" }) }}>
                <button
                  data-testid="cr-transcript-toggle"
                  onClick={() => setOpenSection("transcript")}
                  style={{
                    width: "100%", padding: "11px 14px", background: "#fff", border: "none",
                    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                    fontFamily: "inherit", color: "#111827", flex: "0 0 auto",
                  }}
                >
                  <FileText style={{ width: 13, height: 13, color: "#6b7280" }} />
                  <div style={{ flex: 1, textAlign: "left" }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>Call transcript</div>
                    <div style={{ fontSize: 10.5, color: "#9ca3af" }}>
                      {durLabel}
                      {txWordCount > 0 ? ` · ${txWordCount.toLocaleString()} words` : ""}
                      {transcriptLines.length ? ` · ${transcriptLines.length} lines` : ""}
                    </div>
                  </div>
                  {/* Iter 344.1 — Human Connect quick indicator (always visible,
                      collapsed or expanded). True = genuine live human convo. */}
                  <span
                    data-testid="cr-transcript-human-connect"
                    title={
                      hcVerdict === true ? "AI classified this call as a genuine live human conversation"
                      : hcVerdict === false ? "AI classified this call as not a live human conversation (voicemail / IVR / no-answer)"
                      : "Not yet classified"
                    }
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 4, flexShrink: 0,
                      fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
                      background: hcVerdict === true ? "#dcfce7" : hcVerdict === false ? "#fee2e2" : "#f1f5f9",
                      color: hcVerdict === true ? "#166534" : hcVerdict === false ? "#991b1b" : "#64748b",
                      border: `1px solid ${hcVerdict === true ? "#bbf7d0" : hcVerdict === false ? "#fecaca" : "#e2e8f0"}`,
                    }}
                  >
                    Human connect: {hcVerdict === true ? "True" : hcVerdict === false ? "False" : "—"}
                  </span>
                  {/* Iter 191 (Peter dc10f22f) — full-screen pop-out so
                      the reviewer can read long transcripts without
                      the nested-scroll trap on the inner pane. */}
                  {(transcriptLines.length > 0 || transcriptFullText) && (
                    <span
                      role="button"
                      tabIndex={0}
                      data-testid="cr-transcript-fullscreen"
                      onClick={(e) => { e.stopPropagation(); setTranscriptFullOpen(true); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.stopPropagation(); setTranscriptFullOpen(true); } }}
                      style={{
                        fontSize: 10.5, color: "#2563eb", fontWeight: 600,
                        padding: "3px 9px", border: "1px solid #bfdbfe", borderRadius: 999,
                        background: "#eff6ff", cursor: "pointer", marginRight: 4,
                      }}
                    >
                      View full
                    </span>
                  )}
                  <ChevronDown style={{ width: 14, height: 14, color: "#9ca3af", transform: secOpen("transcript") ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .3s cubic-bezier(0.22,0.61,0.36,1)" }} />
                </button>
                {secOpen("transcript") && (
                  <div style={{
                    padding: 14, borderTop: "1px solid #e5e7eb",
                    // Iter 344 — in accordion mode the open transcript fills
                    // the remaining right-column height and scrolls inside
                    // itself (overscroll contained so the wheel doesn't
                    // bubble past the pane). Iter 344.1 — smooth reveal.
                    flex: "1 1 auto",
                    minHeight: 0,
                    overflowY: "auto",
                    overscrollBehavior: "contain",
                    animation: "crSectionExpand 0.42s cubic-bezier(0.22,0.61,0.36,1)",
                  }} data-testid="cr-transcript-body">
                    {richTx.loading && (
                      <div style={{ fontSize: 11, color: "#6b7280" }}>
                        <Loader2 style={{ width: 12, height: 12, animation: "spin 0.8s linear infinite", verticalAlign: "middle", marginRight: 5 }} />
                        Loading transcript…
                      </div>
                    )}
                    {!richTx.loading && richTx.error && (
                      <div style={{ fontSize: 11, color: "#991b1b" }}>{richTx.error}</div>
                    )}
                    {!richTx.loading && !richTx.error && transcriptLines.length === 0 && !transcriptFullText && (
                      <div style={{ fontSize: 11, color: "#9ca3af", fontStyle: "italic" }}>No transcript available for this call yet.</div>
                    )}
                    {!richTx.loading && !richTx.error && transcriptLines.length === 0 && transcriptFullText && (
                      <div data-testid="cr-transcript-fulltext" style={{ fontSize: 12.5, lineHeight: 1.65, color: "#111827", whiteSpace: "pre-wrap" }}>
                        {transcriptFullText}
                      </div>
                    )}
                    {transcriptLines.length > 0 && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {transcriptLines.map((line, i) => {
                          const speakerRaw = (line.speaker || line.role || "").toString();
                          const isRep = /rep|agent|tatiana|danny|sales|admin/i.test(speakerRaw);
                          const speakerColor = isRep ? "#2563eb" : "#6b7280";
                          return (
                            <div key={i} data-testid={`cr-tx-line-${i}`} style={{ fontSize: 12.5, lineHeight: 1.65, color: "#111827" }}>
                              <span style={{ display: "inline-block", textTransform: "uppercase", fontSize: 10, fontWeight: 700, color: speakerColor, letterSpacing: "0.04em", marginRight: 8 }}>
                                {speakerRaw || (isRep ? "REP" : "LEAD")}
                              </span>
                              {line.text || line.utterance || ""}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </section>
            </div>
          </div>
        )}
      </div>
      {/* Iter 191 (Peter dc10f22f) — Full-screen transcript pop-out.
          Renders OUTSIDE the constrained call-review modal so a long
          transcript can use the full viewport. Same content shape as
          the inline section. */}
      {transcriptFullOpen && (
        <div
          data-testid="cr-transcript-fullmodal"
          onClick={(e) => e.target === e.currentTarget && setTranscriptFullOpen(false)}
          style={{
            position: "fixed", inset: 0, zIndex: 10000,
            background: "rgba(15,23,42,0.72)", backdropFilter: "blur(4px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
          }}
        >
          <div
            style={{
              background: "#fff", borderRadius: 14, width: "min(960px, 95vw)",
              height: "min(90vh, 100%)", display: "flex", flexDirection: "column",
              boxShadow: "0 24px 60px rgba(0,0,0,0.4)",
            }}
          >
            <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", gap: 10 }}>
              <FileText style={{ width: 16, height: 16, color: "#2563eb" }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>Full call transcript</div>
                <div style={{ fontSize: 11, color: "#6b7280" }}>
                  {titleText} · {durLabel}{transcriptLines.length ? ` · ${transcriptLines.length} lines` : ""}
                </div>
              </div>
              <button
                data-testid="cr-transcript-fullmodal-close"
                onClick={() => setTranscriptFullOpen(false)}
                aria-label="Close"
                style={{
                  width: 30, height: 30, borderRadius: "50%", border: "1px solid #e5e7eb",
                  background: "#fff", cursor: "pointer", fontSize: 18, color: "#6b7280",
                }}
              >×</button>
            </div>
            <div style={{ flex: 1, padding: 20, overflowY: "auto", overscrollBehavior: "contain" }}>
              {!richTx.loading && !richTx.error && transcriptLines.length === 0 && transcriptFullText && (
                <div style={{ fontSize: 13.5, lineHeight: 1.7, color: "#111827", whiteSpace: "pre-wrap", fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                  {transcriptFullText}
                </div>
              )}
              {transcriptLines.length > 0 && (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {transcriptLines.map((line, i) => {
                    const speakerRaw = (line.speaker || line.role || "").toString();
                    const isRep = /rep|agent|tatiana|danny|sales|admin/i.test(speakerRaw);
                    const speakerColor = isRep ? "#2563eb" : "#9333ea";
                    return (
                      <div key={i} data-testid={`cr-tx-full-line-${i}`} style={{ fontSize: 13.5, lineHeight: 1.7, color: "#111827" }}>
                        <span style={{ display: "inline-block", textTransform: "uppercase", fontSize: 10.5, fontWeight: 700, color: speakerColor, letterSpacing: "0.05em", marginRight: 10 }}>
                          {speakerRaw || (isRep ? "REP" : "LEAD")}:
                        </span>
                        {line.text || line.utterance || ""}
                      </div>
                    );
                  })}
                </div>
              )}
              {!richTx.loading && !richTx.error && transcriptLines.length === 0 && !transcriptFullText && (
                <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic", textAlign: "center", padding: 40 }}>
                  No transcript available for this call.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── helpers (call-review redesign) ────────────────────────────────────
function CRBadge({ children, bg, color, testid }) {
  return (
    <span
      data-testid={testid}
      style={{
        background: bg, color, fontSize: 11, fontWeight: 500,
        padding: "3px 9px", borderRadius: 20,
      }}
    >
      {children}
    </span>
  );
}
function SecLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: "#9ca3af",
      textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6,
    }}>{children}</div>
  );
}
function ScoreTab({ children, active, onClick, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        padding: "6px 10px", fontSize: 11,
        fontWeight: active ? 500 : 400,
        color: active ? "#2563eb" : "#9ca3af",
        background: "transparent", border: "none",
        borderBottom: `2px solid ${active ? "#2563eb" : "transparent"}`,
        cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit",
        transition: "color .12s",
      }}
    >
      {children}
    </button>
  );
}
function Tile({ label, value, tone, full }) {
  const tones = {
    green:   { color: "#15803d" },
    red:     { color: "#b91c1c" },
    blue:    { color: "#1e40af" },
    amber:   { color: "#92400e" },
    neutral: { color: "#111827" },
  };
  const t = tones[tone || "neutral"];
  return (
    <div style={{
      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 7,
      padding: "7px 9px", gridColumn: full ? "1 / -1" : undefined,
    }}>
      <div style={{ fontSize: 9.5, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 12, color: t.color, fontWeight: 500, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={String(value || "")}>
        {value || "—"}
      </div>
    </div>
  );
}
function Ctx({ k, v }) {
  return (
    <div>
      <div style={{ fontSize: 9.5, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>{k}</div>
      <div style={{ fontSize: 11.5, color: "#111827", marginTop: 1 }}>{v}</div>
    </div>
  );
}
function Tag({ children }) {
  return (
    <span style={{ fontSize: 9.5, padding: "2px 7px", borderRadius: 4, background: "#f3f4f6", color: "#4b5563", fontWeight: 500 }}>
      {children}
    </span>
  );
}
function SummarySection({ label, color, items, testid }) {
  return (
    <div data-testid={testid}>
      <div style={{ fontSize: 10, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 5 }}>{label}</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {items.map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
            <span style={{ width: 4, height: 4, borderRadius: "50%", background: color, marginTop: 7, flexShrink: 0 }} />
            <div style={{ fontSize: 12.5, lineHeight: 1.6, color: "#111827", flex: 1 }}>{String(it).slice(0, 400)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
function LeadScoreCard({ score, sentiment }) {
  const n = typeof score === "number" ? Math.round(score) : null;
  const band = n == null ? "—"
             : n >= 85 ? { label: "Hot",  bg: "#dcfce7", fg: "#15803d" }
             : n >= 70 ? { label: "Warm", bg: "#dbeafe", fg: "#1e40af" }
             : n >= 55 ? { label: "Cool", bg: "#fef3c7", fg: "#92400e" }
             :           { label: "Cold", bg: "#fee2e2", fg: "#991b1b" };
  return (
    <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: "#111827" }}>{n != null ? n : "—"}</div>
        {band !== "—" && (
          <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 999, background: band.bg, color: band.fg }}>{band.label}</span>
        )}
      </div>
      <div style={{ fontSize: 10.5, color: "#9ca3af", marginTop: 4 }}>
        Lead AI score{sentiment ? ` · sentiment ${sentiment}` : ""}
      </div>
      {n != null && (
        <div style={{ marginTop: 8, height: 6, background: "#f3f4f6", borderRadius: 3, overflow: "hidden" }}>
          <div style={{ width: `${Math.min(100, Math.max(0, n))}%`, height: "100%", background: band.fg || "#9ca3af", transition: "width .35s" }} />
        </div>
      )}
    </div>
  );
}

/* ── Iter 141.1 — Coaching feedback panel (text + voice) ────────────── */
function CoachingFeedbackPanel({ apiBase, authHeaders, agentId, activityId, externalId, leadId }) {
  const [items, setItems] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [recordedUrl, setRecordedUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const mediaRecorderRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const elapsedTimerRef = React.useRef(null);

  const reload = React.useCallback(async () => {
    if (!agentId || (!activityId && !externalId)) return;
    try {
      const { data } = await axios.get(`${apiBase}/admin/rep-activity/coaching-feedback`, {
        params: { agent_id: agentId, activity_id: activityId || undefined, external_id: externalId || undefined },
        headers: authHeaders(),
      });
      setItems(data.items || []);
    } catch (e) {
      setItems([]);
    }
  }, [apiBase, authHeaders, agentId, activityId, externalId]);

  useEffect(() => { reload(); }, [reload]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach((t) => t.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
      setElapsed(0);
      elapsedTimerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Couldn't access the microphone. Check browser permissions.");
    }
  };

  const stopRecording = () => {
    try {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
        mediaRecorderRef.current.stop();
      }
    } catch (e) { /* ignore */ }
    if (elapsedTimerRef.current) { clearInterval(elapsedTimerRef.current); elapsedTimerRef.current = null; }
    setRecording(false);
  };

  const clearRecording = () => {
    setRecordedBlob(null);
    if (recordedUrl) URL.revokeObjectURL(recordedUrl);
    setRecordedUrl(null);
    setElapsed(0);
  };

  const send = async () => {
    if (!text.trim() && !recordedBlob) {
      toast.error("Add text or record a voice note.");
      return;
    }
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("agent_id", agentId);
      if (activityId) fd.append("activity_id", activityId);
      if (externalId) fd.append("external_id", externalId);
      if (leadId) fd.append("lead_id", leadId);
      if (text.trim()) fd.append("text", text.trim());
      if (recordedBlob) fd.append("audio", recordedBlob, "voice-note.webm");
      await axios.post(`${apiBase}/admin/rep-activity/coaching-feedback`, fd, {
        headers: { ...authHeaders(), "Content-Type": "multipart/form-data" },
      });
      setText("");
      clearRecording();
      toast.success("Coaching feedback sent — the rep will see it in their inbox.");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send feedback");
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await axios.delete(`${apiBase}/admin/rep-activity/coaching-feedback/${id}`, { headers: authHeaders() });
      reload();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const fmtElapsed = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const sendDisabled = sending || (!text.trim() && !recordedBlob);

  return (
    <div data-testid="coaching-feedback-panel" style={{ border: "0.5px solid #fcd34d", borderRadius: 10, background: "linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)", padding: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <MessageCircle style={{ width: 14, height: 14, color: "#b45309" }} />
        <div style={{ fontSize: 12, fontWeight: 700, color: "#78350f" }}>Coaching feedback</div>
        <div style={{ fontSize: 10, color: "#92400e", marginLeft: "auto" }}>The rep sees this in their notifications.</div>
      </div>

      <div style={{ background: "#fff", border: "0.5px solid #fde68a", borderRadius: 8, padding: 10 }}>
        <textarea
          data-testid="coaching-text-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Type quick coaching feedback for this call…"
          rows={3}
          style={{ width: "100%", fontSize: 12, fontFamily: "inherit", padding: 8, border: "0.5px solid #e2e8f0", borderRadius: 6, resize: "vertical", color: "#0f172a", outline: "none", boxSizing: "border-box" }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
          {!recording && !recordedUrl && (
            <button
              data-testid="coaching-record-start"
              type="button"
              onClick={startRecording}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#dc2626", border: "0.5px solid #fecaca", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}
            >
              <Mic style={{ width: 12, height: 12 }} /> Record voice
            </button>
          )}
          {recording && (
            <button
              data-testid="coaching-record-stop"
              type="button"
              onClick={stopRecording}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", fontSize: 11, fontWeight: 600, background: "#dc2626", color: "#fff", border: 0, borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}
            >
              <Square style={{ width: 12, height: 12 }} /> Stop · {fmtElapsed(elapsed)}
            </button>
          )}
          {recordedUrl && !recording && (
            <>
              <audio data-testid="coaching-recorded-preview" controls src={recordedUrl} style={{ height: 28 }} />
              <button
                data-testid="coaching-record-clear"
                type="button"
                onClick={clearRecording}
                style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 8px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#64748b", border: "0.5px solid #e2e8f0", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}
              >
                <Trash2 style={{ width: 11, height: 11 }} /> Discard
              </button>
            </>
          )}
          <button
            data-testid="coaching-send"
            type="button"
            onClick={send}
            disabled={sendDisabled}
            style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px", fontSize: 11, fontWeight: 700, background: sendDisabled ? "#cbd5e1" : "#0f766e", color: "#fff", border: 0, borderRadius: 6, cursor: sendDisabled ? "not-allowed" : "pointer", fontFamily: "inherit" }}
          >
            {sending ? <Loader2 style={{ width: 12, height: 12, animation: "spin 1s linear infinite" }} /> : <Send style={{ width: 12, height: 12 }} />}
            Send
          </button>
        </div>
      </div>

      {items === null && (
        <div style={{ fontSize: 11, color: "#92400e", marginTop: 10 }}>Loading feedback…</div>
      )}
      {items && items.length > 0 && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#78350f", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Previous feedback ({items.length})
          </div>
          {items.map((fb) => (
            <CoachingFeedbackItem
              key={fb.id}
              fb={fb}
              apiBase={apiBase}
              authHeaders={authHeaders}
              onDelete={remove}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CoachingFeedbackItem({ fb, apiBase, authHeaders, onDelete }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loadingAudio, setLoadingAudio] = useState(false);
  const hasAudio = !!fb.audio;

  const loadAudio = async () => {
    if (audioUrl || loadingAudio) return;
    setLoadingAudio(true);
    try {
      const res = await axios.get(`${apiBase}/admin/rep-activity/coaching-feedback/${fb.id}/audio`, {
        headers: authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      setAudioUrl(url);
    } catch (e) {
      toast.error("Couldn't load audio");
    } finally {
      setLoadingAudio(false);
    }
  };

  return (
    <div data-testid={`coaching-fb-${fb.id}`} style={{ background: "#fff", border: "0.5px solid #fde68a", borderRadius: 8, padding: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }}>
          {fb.author_name || "Manager"}
          <span style={{ fontSize: 10, fontWeight: 400, color: "#94a3b8", marginLeft: 6 }}>
            · {fb.created_at ? new Date(fb.created_at).toLocaleString() : ""}
          </span>
        </div>
        <button
          data-testid={`coaching-fb-delete-${fb.id}`}
          type="button"
          onClick={() => onDelete(fb.id)}
          title="Delete"
          style={{ background: "transparent", border: 0, cursor: "pointer", color: "#94a3b8", padding: 4 }}
        >
          <Trash2 style={{ width: 12, height: 12 }} />
        </button>
      </div>
      {fb.text && (
        <div style={{ fontSize: 12, color: "#334155", whiteSpace: "pre-wrap", marginBottom: hasAudio ? 8 : 0 }}>
          {fb.text}
        </div>
      )}
      {hasAudio && (
        <div>
          {audioUrl ? (
            <audio controls src={audioUrl} style={{ width: "100%", height: 30 }} />
          ) : (
            <button
              data-testid={`coaching-fb-play-${fb.id}`}
              type="button"
              onClick={loadAudio}
              disabled={loadingAudio}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", fontSize: 11, fontWeight: 600, background: "#fff", color: "#0f766e", border: "0.5px solid #99f6e4", borderRadius: 6, cursor: "pointer", fontFamily: "inherit" }}
            >
              {loadingAudio ? <Loader2 style={{ width: 11, height: 11, animation: "spin 1s linear infinite" }} /> : <Play style={{ width: 11, height: 11 }} />}
              Listen{fb.audio?.size ? ` (${Math.ceil((fb.audio.size || 0) / 1024)} KB)` : ""}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

